import {
  DEFAULT_ONLINE_FIELD,
  MATCH_MODES,
  ONLINE_MODE_IDS,
  PITCH_SKIN_IDS,
  PITCH_SKINS,
  fieldSeatsForMode,
  maxHumanSlots,
  type OnlineFieldId,
  type OnlineModeId,
  type OnlinePresence,
  type OnlineRoom,
  type SeatOccupant,
  type RoomVisibility,
  type WireInputFrame,
  weeklyStatus,
  type MatchDrawRule,
  type RoomChatEntry,
  type FriendEntry,
  MAX_CHAT_CHARS,
} from "@golazo/shared";
import type { OnlineClient, ConnectionState } from "../online/client.js";
import type { RoomChat } from "../online/roomChat.js";
import type { FriendsStore, FriendsState } from "../online/friends.js";
import type { PartyStore } from "../online/party.js";
import type { LobbyChat } from "../online/lobbyChat.js";
import { editNickname, loadNickname, onNicknameChange } from "../online/nickname.js";
import type { DirectMessages } from "../online/directMessages.js";
import { loadRecentPlayers } from "../online/recentPlayers.js";
import { chatLineNode } from "./roomChatView.js";
import { t } from "../i18n/index.js";
import { createNetworkRoomStore } from "../online/networkStore.js";
import {
  createLocalRoomStore,
  occupiedCount,
  type RoomStore,
} from "../online/rooms.js";
import "./menu.css";
import "./lobbyHub.css";
import { tierMarkHtml, tierMarkNode } from "./tierMark.js";
import { lastSeenText, vsChip, whereText } from "./friendText.js";
import { roomInviteText, roomInviteUrl, shareText, whatsappUrl } from "./share.js";

const LOBBY_BACKDROP_HTML = `<div class="lobby-bg" aria-hidden="true"></div>`;

/**
 * La jornada semanal, apagada por ahora (2026-09-26): ocupaba el segundo puesto
 * de la pantalla siendo algo que pasa una vez por semana. La logica sigue
 * entera —el servidor aplica la jornada en la partida rapida— y se vuelve a
 * encender con esto.
 */
const SHOW_WEEKLY = false;

/** Los huecos de una sala, dibujados: encendidos los ocupados. */
function seatPips(taken: number, total: number): string {
  let out = "";
  for (let i = 0; i < total; i++) {
    out += `<i class="room-pip${i < taken ? " is-taken" : ""}"></i>`;
  }
  return out;
}

/**
 * QUIEN HAY DENTRO.
 *
 * Es el dato que de verdad decide si entras a una sala y el unico que no
 * estaba: "3/6" dice cuanta gente hay, no QUIEN. Con nombres se ve de un
 * vistazo si esta tu amigo, y se distingue una sala con personas de una con
 * un hueco suelto entre bots.
 *
 * Se corta a dos nombres y el resto se resume. Poner los seis convierte la
 * lista en un muro de texto — y el encargo era que se notara trabajado, no que
 * fuera intrusivo.
 */
function whoIsIn(room: OnlineRoom, max: number, skip: readonly string[] = []): string {
  const all = Object.values(room.seats)
    .filter((o): o is SeatOccupant => Boolean(o))
    .map((o) => o.nickname.trim())
    .filter((n) => n.length > 0);
  // Los amigos ya van delante, en dorado: repetirlos aqui es ruido.
  const names = all.filter((n) => !skip.includes(n));
  if (names.length === 0) {
    const bots = Math.max(0, max - all.length);
    return bots > 0 ? `${bots} ${bots === 1 ? t("bot") : t("bots")}` : "";
  }
  const shown = names.slice(0, 2).map(escapeHtml).join(", ");
  const rest = names.length - 2;
  const bots = Math.max(0, max - all.length);
  const tail = rest > 0 ? ` +${rest}` : "";
  // Los bots solo se nombran si los hay: "con 2 bots" en una sala llena seria
  // ruido, y en una vacia es justo lo que quieres saber antes de entrar.
  const botPart = bots > 0 ? ` · ${bots} ${bots === 1 ? t("bot") : t("bots")}` : "";
  return `${shown}${tail}${botPart}`;
}

/**
 * Hace cuanto arranco el partido, en corto. Solo para salas en partida.
 *
 * Desde `startedAt` y no desde que se creo la sala: la sala ya sobrevive a sus
 * partidos, y una que lleva tres diria "minuto 14" de un partido de cinco.
 */
function runningFor(room: OnlineRoom): string {
  const mins = Math.max(0, Math.floor((Date.now() - (room.startedAt ?? room.createdAt)) / 60000));
  if (mins < 1) return t("acaba de empezar");
  return t("va por el minuto {n}", { n: mins });
}


export interface OnlinePlayRequest {
  room: OnlineRoom;
  nickname: string;
  localPlayerId: string;
  seed: number;
  roomId: string;
  client?: OnlineClient;
  lateJoinHistory?: Array<{ tick: number; frames: Record<string, WireInputFrame> }>;
  /** Jornada semanal con la que se juega, si el server la ha estampado. */
  weeklyRuleId?: string;
  /** Eres la unica persona: se juega en local hasta que entre alguien. */
  solo?: boolean;
}

export interface OnlineLobbyOptions {
  client?: OnlineClient;
  /** Chat de la sala. Compartido con el partido: ver `online/roomChat.ts`. */
  chat?: RoomChat;
  /** Amigos, en vivo. Vive fuera del lobby: tambien avisa dentro del partido. */
  friends?: FriendsStore;
  /** Chat de quien mira la lista de salas. */
  lobbyChat?: LobbyChat;
  /** Abre la ficha de un amigo comparada con la tuya. */
  openFriend?: (clientId: string, nickname: string) => void;
  /** Privados con los amigos. */
  directMessages?: DirectMessages;
  /** Tu grupo: se ve en la cabecera, y "Jugar ya" lo lleva entero. */
  party?: PartyStore;
  /** Abrir el panel de amigos y grupo (el de la capa social). */
  openSocial?: () => void;
  store?: RoomStore;
  serverUrl?: string;
  useLocalStore?: boolean;
}

export interface ShowOptions {
  /** Sala a la que entrar nada mas abrir, cuando se llega por un enlace. */
  joinRoomId?: string;
  /** Modo con el que abrir la partida rapida. */
  quickMode?: OnlineModeId;
  /** Entrar en la cola directamente, sin tener que pulsar. */
  autoQueue?: boolean;
  /**
   * Volver a una sala en la que ya estas: al acabar el partido, o cuando el
   * anfitrion te saca al banquillo. Se vuelve a pedir la entrada, que es lo que
   * le dice al servidor que ya no estas en la pantalla de resultados.
   */
  roomId?: string;
  /** Aviso que enseñar al abrir ("El anfitrión ha parado el partido"). */
  notice?: string;
  /** Abrir la conversacion con este amigo (desde su ficha, fuera del lobby). */
  dm?: string;
  /** Montar una sala para amigos e invitar a este (o invitarle a la tuya). */
  playWith?: string;
  /** Sala para amigos nueva, con el enlace a la vista para pasarlo fuera. */
  inviteNew?: boolean;
}

export interface OnlineLobbyHandle {
  show(opts?: ShowOptions): Promise<OnlinePlayRequest | { back: true }>;
  hide(): void;
  /** Esta a la vista ahora mismo. */
  isVisible(): boolean;
  /** Abre la conversacion privada con un amigo (con el lobby ya abierto). */
  openChat(clientId: string): void;
  /**
   * Entra en una sala por id, como un enlace de invitacion. `pass`: vienes
   * invitado o siguiendo a tu grupo, y el servidor te deja pasar sin
   * contraseña: no se pregunta.
   */
  joinRoom(roomId: string, pass?: boolean): void;
  /** Hay una sala tuya abierta a la que invitar. */
  canInvite(): boolean;
  /** Invita a un amigo a esa sala. */
  inviteFriend(clientId: string): void;
  /** Jugar con un amigo: a tu sala si tienes, y si no se monta una y se le invita. */
  playWith(clientId: string): void;
  /** Sala para amigos nueva con el enlace a la vista. */
  inviteNew(): void;
  dispose(): void;
}

function clampNick(raw: string): string {
  const n = raw.trim().slice(0, 16);
  return n || t("Jugador");
}

function connectionLabel(state: ConnectionState): string {
  switch (state) {
    case "online":
      return t("EN LÍNEA");
    case "connecting":
      return t("CONECTANDO…");
    case "reconnecting":
      return t("RECONECTANDO…");
    case "elsewhere":
      return t("EN OTRA PESTAÑA");
    case "outdated":
      return t("RECARGA: VERSIÓN NUEVA");
    default:
      return t("SIN CONEXIÓN");
  }
}

const VISIBILITY_LABEL: Record<RoomVisibility, string> = {
  public: t("pública"),
  private: t("privada"),
  link: t("por enlace"),
};

function roomVisibility(room: OnlineRoom): RoomVisibility {
  return room.visibility ?? "public";
}

export function createOnlineLobby(
  root: HTMLElement = document.body,
  options: OnlineLobbyOptions = {},
): OnlineLobbyHandle {
  const client = options.client ?? null;
  const chat = options.chat ?? null;
  const friendsStore = options.friends ?? null;
  const lobbyChat = options.lobbyChat ?? null;
  const dms = options.directMessages ?? null;
  const store: RoomStore =
    options.store ??
    (client && !options.useLocalStore
      ? createNetworkRoomStore({ client })
      : createLocalRoomStore());

  const overlay = document.createElement("div");
  overlay.id = "online-lobby";
  overlay.className = "hub-overlay hub-overlay--scroll";
  overlay.innerHTML = `
    ${LOBBY_BACKDROP_HTML}

    <!--
      EL LOBBY, a pantalla completa.

      Antes era una columna estrecha y centrada con la mitad de la pantalla
      vacia a los lados, y las salas —que es a lo que se viene— en una caja de
      60 px al fondo. Ahora cada zona existe por algo y ocupa lo que merece:

        izquierda  → lo que HACES: jugar ya (una franja) y las salas (todo el
                     alto que quede, con su propio scroll, lista densa pensada
                     para doscientas salas y no para dos);
        derecha    → con QUIEN: el chat del lobby y tus amigos.

      En movil, las mismas tres cosas en pestañas: Salas, Chat, Amigos.
    -->
    <div class="menu-shell lobby-shell lobby-shell--hub" data-lobby-shell data-view="rooms" data-social="chat">
      <header class="lobby-topbar lobby-topbar--hub">
        <button type="button" class="lobby-back lobby-back--ghost">${t("← Menú")}</button>
        <div class="lobby-topbar-mid">
          <span class="lobby-topbar-title">${t("Multijugador")}</span>
          <span class="conn-pill conn-pill--compact" data-conn-pill aria-live="polite">
            <span class="conn-dot" data-conn-dot></span>
            <span data-conn-label>…</span>
          </span>
        </div>
        <!--
          El pulso del servidor, en la barra: es un dato de contexto, no una
          seccion. Lo que importa de un vistazo es si hay gente y si juega.
        -->
        <div class="lobby-pulse lobby-pulse--inline" data-pulse aria-live="polite">
          <span class="lobby-pulse-live"><i class="lobby-pulse-dot"></i>${t("EN VIVO")}</span>
          <span class="lobby-pulse-stat"><strong data-pulse-playing>0</strong><span>${t("jugando")}</span></span>
          <span class="lobby-pulse-stat"><strong data-pulse-online>0</strong><span>${t("conectados")}</span></span>
          <span class="lobby-pulse-stat"><strong data-pulse-rooms>0</strong><span>${t("salas")}</span></span>
        </div>
        <!-- Tu grupo, como la escuadra de Fortnite: quien viene contigo. -->
        <button type="button" class="lobby-party" data-lobby-party hidden></button>
        <button type="button" class="lobby-identity" data-identity title="${t("Cambiar tu apodo")}" aria-label="${t("Cambiar tu apodo")}">
          <span class="lobby-identity-at">@</span>
          <span class="lobby-identity-name" data-identity-name></span>
          <span class="lobby-identity-edit" aria-hidden="true">✎</span>
        </button>
      </header>

      <!-- La jornada semanal, apagada por ahora (ver SHOW_WEEKLY). -->
      <button type="button" class="weekly-card lobby-weekly" data-weekly hidden>
        <span class="weekly-kicker" data-weekly-kicker>${t("La jornada")}</span>
        <strong class="weekly-title" data-weekly-title></strong>
        <span class="weekly-blurb" data-weekly-blurb></span>
        <span class="weekly-clock" data-weekly-clock></span>
      </button>

      <nav class="lobby-tabs" data-lobby-tabs aria-label="${t("Secciones")}">
        <button type="button" class="lobby-tab is-active" data-go="rooms">${t("Salas")}</button>
        <button type="button" class="lobby-tab" data-go="chat">${t("Chat")} <i class="lobby-badge" data-badge-chat hidden></i></button>
        <button type="button" class="lobby-tab" data-go="friends">${t("Amigos")} <i class="lobby-badge" data-badge-friends hidden></i></button>
      </nav>

      <div class="lobby-hub">
        <main class="lobby-col lobby-col--play">
          <!--
            Jugar ya: una franja, no un bloque. Modo y boton en la misma linea;
            es la puerta rapida, no la protagonista.
          -->
          <section class="lobby-quick lobby-quick--strip" data-quick hidden>
            <h2 class="lobby-strip-title">${t("Jugar ya")}</h2>
            <div class="lobby-quick-modes" data-quick-modes role="group" aria-label="${t("Modo de partida rápida")}"></div>
            <p class="lobby-quick-status" data-quick-status aria-live="polite"></p>
            <button type="button" class="menu-play lobby-quick-btn" data-quick-btn>${t("JUGAR YA")}</button>
          </section>

          <section class="lobby-main-panel lobby-rooms">
            <div class="lobby-list-head">
              <h2 class="lobby-list-title">${t("Salas")} <span class="lobby-list-count" data-room-count></span></h2>
              <label class="lobby-search">
                <span class="lobby-search-icon" aria-hidden="true">⌕</span>
                <input type="search" data-room-search maxlength="24"
                       placeholder="${t("Buscar sala…")}" aria-label="${t("Buscar sala por nombre")}" />
              </label>
              <div class="lobby-filters" data-room-filters role="group" aria-label="${t("Filtros")}">
                <button type="button" class="lobby-filter is-active" data-filter="all">${t("Todas")}</button>
                <button type="button" class="lobby-filter" data-filter="open">${t("Con hueco")}</button>
                <button type="button" class="lobby-filter" data-filter="live">${t("En partida")}</button>
                <button type="button" class="lobby-filter" data-filter="friends">${t("Amigos")}</button>
              </div>
              <select class="lobby-mode-filter" data-mode-filter aria-label="${t("Filtrar por modo")}">
                <option value="">${t("Cualquier modo")}</option>
              </select>
              <div class="lobby-list-actions">
                <button type="button" class="lobby-refresh" aria-label="${t("Actualizar salas")}">↻</button>
                <button type="button" class="lobby-btn-ghost lobby-invite-open" data-invite-open disabled>${t("Con amigos")}</button>
                <button type="button" class="menu-play lobby-create-open" data-create-open disabled>${t("+ Crear sala")}</button>
              </div>
            </div>

            <!-- Cabecera de columnas: la lista es una tabla que se escanea, no tarjetas que se leen. -->
            <div class="room-cols" aria-hidden="true">
              <span>${t("Sala")}</span>
              <span>${t("Modo")}</span>
              <span>${t("Jugadores")}</span>
              <span>${t("Estado")}</span>
              <span></span>
            </div>
            <div class="room-list room-list--primary room-list--table" role="list"></div>
          </section>
        </main>

        <aside class="lobby-col lobby-col--social">
          <div class="lobby-social-tabs" role="tablist">
            <button type="button" class="lobby-social-tab is-active" role="tab" data-social-tab="chat">
              ${t("Chat")} <span class="lobby-social-meta" data-chat-here></span>
            </button>
            <button type="button" class="lobby-social-tab" role="tab" data-social-tab="friends">
              ${t("Amigos")} <span class="lobby-social-meta" data-friends-online></span>
              <i class="lobby-badge" data-badge-requests hidden></i>
            </button>
          </div>

          <!-- Chat del lobby: para quien aun no tiene sala. "¿Alguien 3v3 con prórroga?" -->
          <section class="lobby-chat" data-social-pane="chat">
            <ol class="lobby-chat-log" data-lobby-log aria-live="polite"></ol>
            <form class="lobby-chat-form" data-lobby-form>
              <input type="text" class="room-chat-input" data-lobby-input maxlength="${MAX_CHAT_CHARS}"
                     autocomplete="off" enterkeyhint="send"
                     placeholder="${t("Habla con quien está en el lobby…")}" aria-label="${t("Mensaje para el lobby")}" />
              <button type="submit" class="menu-play room-chat-send">${t("Enviar")}</button>
            </form>
          </section>

          <!-- Amigos: tu codigo, añadir, solicitudes y quien esta donde. -->
          <section class="lobby-friends" data-social-pane="friends" hidden>
            <div class="friend-code">
              <span class="friend-code-label">${t("Tu código de amigo")}</span>
              <strong class="friend-code-value" data-friend-code>····-····</strong>
              <button type="button" class="lobby-btn-ghost friend-code-copy" data-friend-code-copy>${t("Copiar")}</button>
            </div>
            <form class="friend-add" data-friend-add>
              <!-- Apodo o codigo: desde que los apodos son unicos, los dos encuentran a alguien. -->
              <input type="text" class="room-chat-input" data-friend-add-input maxlength="16" autocomplete="off"
                     autocapitalize="off" spellcheck="false"
                     placeholder="${t("Apodo o código de amigo")}" aria-label="${t("Apodo o código de amigo")}" />
              <button type="submit" class="menu-play">${t("Añadir")}</button>
            </form>
            <div class="friend-lists" data-friend-lists></div>

            <!--
              La conversacion privada con un amigo. Ocupa el panel entero de
              amigos (no una ventana aparte): en movil es la unica forma de que
              quepa, y en escritorio deja las salas a la vista mientras hablas.
            -->
            <div class="friend-chat" data-dm hidden>
              <header class="friend-chat-head">
                <button type="button" class="friend-chat-back" data-dm-back aria-label="${t("Volver a amigos")}">‹</button>
                <div class="friend-chat-who">
                  <strong data-dm-name></strong>
                  <small data-dm-status></small>
                </div>
                <div class="friend-chat-actions" data-dm-actions></div>
              </header>
              <div class="friend-chat-more" data-dm-menu hidden></div>
              <ol class="lobby-chat-log friend-chat-log" data-dm-log aria-live="polite"></ol>
              <form class="lobby-chat-form" data-dm-form>
                <input type="text" class="room-chat-input" data-dm-input maxlength="${MAX_CHAT_CHARS}"
                       autocomplete="off" enterkeyhint="send" aria-label="${t("Mensaje privado")}" />
                <button type="submit" class="menu-play room-chat-send">${t("Enviar")}</button>
              </form>
            </div>
          </section>
        </aside>
      </div>
    </div>

    <div class="lobby-modal is-hidden" data-create-modal aria-hidden="true">
      <button type="button" class="lobby-modal-backdrop" data-create-close tabindex="-1" aria-label="${t("Cerrar")}"></button>
      <div class="lobby-modal-sheet lobby-modal-sheet--create" role="dialog" aria-labelledby="create-modal-title">
        <header class="lobby-modal-header">
          <div>
            <p class="lobby-modal-kicker">${t("Configuración")}</p>
            <h2 id="create-modal-title">${t("Nueva sala")}</h2>
          </div>
          <button type="button" class="lobby-modal-close" data-create-close aria-label="${t("Cerrar")}">×</button>
        </header>

        <!--
          PESTAÑAS, no una lista larga.
          El formulario crecio hasta no caber en el modal y la mitad de los
          ajustes quedaba bajo el pliegue: se reporto como "no veo la
          personalizacion de los tiempos". Partirlo en tres es lo mismo que
          ya hace Personalizar, y agrupa por lo que se viene a decidir:
          quien entra, como se juega y donde.
        -->
        <div class="lobby-modal-tabs" role="tablist" aria-label="${t("Ajustes de la sala")}">
          <button type="button" class="customize-tab is-active" role="tab" data-ctab="sala">${t("Sala")}</button>
          <button type="button" class="customize-tab" role="tab" data-ctab="partido">${t("Partido")}</button>
          <button type="button" class="customize-tab" role="tab" data-ctab="campo">${t("Campo")}</button>
        </div>

        <div class="lobby-modal-body">
          <section class="lobby-cpane" data-cpane="sala">
          <div class="lobby-field">
            <label class="lobby-field-label" for="create-room-name">${t("Nombre")}</label>
            <input id="create-room-name" type="text" class="lobby-field-input" maxlength="24" placeholder="${t("Mi sala")}" />
          </div>

          <div class="lobby-field">
            <span class="lobby-field-label">${t("Visibilidad")}</span>
            <div class="lobby-segment" role="group" aria-label="${t("Visibilidad")}">
              <button type="button" class="lobby-segment-btn is-active" data-visibility="public">${t("Pública")}</button>
              <button type="button" class="lobby-segment-btn" data-visibility="private">${t("Privada")}</button>
            </div>
            <p class="lobby-field-hint">${t("Las privadas piden contraseña al crear y al entrar.")}</p>
          </div>

          </section>

          <section class="lobby-cpane is-hidden" data-cpane="partido">
          <div class="lobby-field">
            <span class="lobby-field-label">${t("Jugadores")}</span>
            <div class="lobby-mode-grid" data-mode-grid role="group" aria-label="${t("Modo de juego")}"></div>
          </div>

          <!--
            El campo lo elige quien abre la sala, y lo ven TODOS.
            Es la unica parte del aspecto que no puede ser local: si cada uno
            pintase su cesped, dos jugadores estarian hablando de sitios
            distintos. Y en una sala montada para jugar con amigos, el escenario
            es media gracia.
          -->
          <!--
            DURACION. Va en la sala, no en la preferencia de cada uno: el reloj
            es estado de la simulacion y dos duraciones distintas terminarian el
            partido en ticks distintos. Lo elige quien abre y lo juegan todos.
          -->
          <div class="lobby-field">
            <span class="lobby-field-label">${t("Duración de cada tiempo")}</span>
            <div class="lobby-minutes" role="group" aria-label="${t("Duración de cada tiempo")}">
              <button type="button" data-minutes="2">2</button>
              <button type="button" data-minutes="5" class="is-active">5</button>
              <button type="button" data-minutes="8">8</button>
              <button type="button" data-minutes="12">12</button>
              <button type="button" data-minutes="20">20</button>
              <span class="lobby-minutes-unit">${t("min")}</span>
            </div>
          </div>

          <!--
            Tiempos y empate COMPARTEN FILA.
            En vertical eran dos bloques mas y caian bajo el pliegue del modal:
            existian y no se veian, que para quien juega es lo mismo que no
            existir. Juntos ocupan lo que ocupaba uno.
          -->
          <div class="lobby-field lobby-field--split">
            <div>
              <span class="lobby-field-label">${t("Tiempos")}</span>
              <div class="lobby-minutes" role="group" aria-label="${t("Número de tiempos")}">
                <button type="button" data-periods="1" class="is-active">1</button>
                <button type="button" data-periods="2">2</button>
                <button type="button" data-periods="3">3</button>
                <button type="button" data-periods="4">4</button>
              </div>
            </div>
            <div>
              <span class="lobby-field-label">${t("Si acaban empatados")}</span>
              <div class="lobby-segment" role="group" aria-label="${t("Si acaban empatados")}">
                <button type="button" class="lobby-segment-btn is-active" data-draw="golden">${t("Gol de oro")}</button>
                <button type="button" class="lobby-segment-btn" data-draw="draw">${t("Empate")}</button>
              </div>
            </div>
          </div>
          <p class="lobby-field-hint" data-total-hint></p>
          </section>

          <section class="lobby-cpane is-hidden" data-cpane="campo">

          <div class="lobby-field">
            <span class="lobby-field-label">${t("Campo")}</span>
            <div class="lobby-pitch-grid" data-field-grid role="radiogroup" aria-label="${t("Campo de la sala")}"></div>
            <p class="lobby-field-hint" data-field-hint></p>
          </div>
        </div>
          </section>

        <footer class="lobby-modal-footer">
          <button type="button" class="lobby-btn-ghost" data-create-close>${t("Cancelar")}</button>
          <button type="button" class="menu-play lobby-create-submit" data-create-submit>${t("Crear sala")}</button>
        </footer>
      </div>
    </div>

    <div class="lobby-modal is-hidden" data-password-modal aria-hidden="true">
      <button type="button" class="lobby-modal-backdrop" data-password-close tabindex="-1" aria-label="${t("Cerrar")}"></button>
      <div class="lobby-modal-sheet lobby-modal-sheet--password" role="dialog" aria-labelledby="password-modal-title">
        <header class="lobby-modal-header">
          <div>
            <p class="lobby-modal-kicker" data-password-kicker>${t("Contraseña")}</p>
            <h2 id="password-modal-title" data-password-title>${t("Contraseña de la sala")}</h2>
          </div>
          <button type="button" class="lobby-modal-close" data-password-close aria-label="${t("Cerrar")}">×</button>
        </header>
        <div class="lobby-modal-body">
          <div class="lobby-field">
            <label class="lobby-field-label" for="room-password">${t("Contraseña")}</label>
            <input id="room-password" type="password" class="lobby-field-input" maxlength="32" placeholder="••••••" autocomplete="off" />
          </div>
        </div>
        <footer class="lobby-modal-footer">
          <button type="button" class="lobby-btn-ghost" data-password-close>${t("Cancelar")}</button>
          <button type="button" class="menu-play" data-password-submit>${t("Confirmar")}</button>
        </footer>
      </div>
    </div>

    <div class="room-modal is-hidden" data-room-modal>
      <!--
        La sala, como en HaxBall: a la izquierda quien juega y quien espera,
        a la derecha la conversacion. La sala es la gente, no los asientos: si
        no hay hueco se espera en el banquillo, y el anfitrion decide.
      -->
      <div class="room-modal-card room-modal-card--hall">
        <div class="room-main">
          <header class="room-head">
            <div class="room-head-main">
              <h2 data-room-title></h2>
              <p class="room-meta" data-room-meta></p>
            </div>
            <span class="room-badge" data-room-badge></span>
          </header>

          <p class="room-live is-hidden" data-room-live role="status">
            <span class="room-live-dot" aria-hidden="true"></span>
            <span data-room-live-text></span>
          </p>

          <div class="room-teams">
            <section class="room-team room-team--home">
              <h3 class="room-team-head">
                <span class="room-team-name">${t("Local")}</span>
                <span class="room-team-count" data-count-home></span>
              </h3>
              <ul class="seat-col" data-seats-home></ul>
            </section>
            <span class="room-vs" aria-hidden="true">VS</span>
            <section class="room-team room-team--away">
              <h3 class="room-team-head">
                <span class="room-team-name">${t("Visitante")}</span>
                <span class="room-team-count" data-count-away></span>
              </h3>
              <ul class="seat-col" data-seats-away></ul>
            </section>
          </div>
          <p class="seat-gk-note">${t("Los porteros los lleva siempre la IA.")}</p>

          <section class="room-bench">
            <h3 class="room-bench-head">
              <span>${t("Banquillo")}</span>
              <span class="room-team-count" data-bench-count></span>
            </h3>
            <ul class="seat-col bench-col" data-bench></ul>
            <p class="bench-empty" data-bench-empty>${t("Nadie esperando. Si la sala se llena, los que lleguen esperan aquí.")}</p>
          </section>

          <div class="invite-block is-hidden" data-invite-block>
            <p class="invite-head">${t("Pásales este enlace")}</p>
            <!--
              Compartir primero: en el movil abre WhatsApp o lo que se use, que
              es como se llama de verdad a un amigo. Copiar queda para el resto.
            -->
            <div class="invite-row">
              <input type="text" class="invite-url" data-invite-url readonly />
              <button type="button" class="menu-play invite-share" data-invite-share hidden>${t("Compartir")}</button>
              <a class="invite-wa" data-invite-wa target="_blank" rel="noopener">WhatsApp</a>
              <button type="button" class="lobby-btn-ghost invite-copy" data-invite-copy>${t("Copiar")}</button>
            </div>
            <p class="invite-note">${t("Al abrirlo entran en tu sala y os hacéis amigos. No sale en ninguna lista.")}</p>
          </div>

          <p class="seat-note" data-seat-note></p>
          <div class="room-modal-actions">
            <button type="button" class="lobby-back" data-leave>${t("Salir")}</button>
            <button type="button" class="lobby-back lobby-back--ghost" data-self-seat hidden></button>
            <button type="button" class="menu-play" data-start>${t("Jugar")}</button>
          </div>
        </div>

        <aside class="room-chat" aria-label="${t("Chat de la sala")}">
          <h3 class="room-chat-head">${t("Chat")}</h3>
          <ol class="room-chat-log" data-chat-log aria-live="polite"></ol>
          <form class="room-chat-form" data-chat-form>
            <input
              type="text"
              class="room-chat-input"
              data-chat-input
              maxlength="${MAX_CHAT_CHARS}"
              autocomplete="off"
              enterkeyhint="send"
              placeholder="${t("Escribe a la sala…")}"
              aria-label="${t("Mensaje para la sala")}"
            />
            <button type="submit" class="menu-play room-chat-send">${t("Enviar")}</button>
          </form>
        </aside>
      </div>
    </div>
  `;

  root.appendChild(overlay);

  const identityName = overlay.querySelector("[data-identity-name]") as HTMLElement;
  const roomNameInput = overlay.querySelector("#create-room-name") as HTMLInputElement;
  const modeGrid = overlay.querySelector("[data-mode-grid]") as HTMLElement;
  const roomList = overlay.querySelector(".room-list") as HTMLElement;
  const createModal = overlay.querySelector("[data-create-modal]") as HTMLElement;
  const passwordModal = overlay.querySelector("[data-password-modal]") as HTMLElement;
  const passwordInput = overlay.querySelector("#room-password") as HTMLInputElement;
  const passwordTitle = overlay.querySelector("[data-password-title]") as HTMLElement;
  const passwordKicker = overlay.querySelector("[data-password-kicker]") as HTMLElement;
  const passwordSubmitBtn = overlay.querySelector("[data-password-submit]") as HTMLButtonElement;
  const modal = overlay.querySelector("[data-room-modal]") as HTMLElement;
  const seatsHome = overlay.querySelector("[data-seats-home]") as HTMLElement;
  const seatsAway = overlay.querySelector("[data-seats-away]") as HTMLElement;
  const createOpenBtn = overlay.querySelector("[data-create-open]") as HTMLButtonElement;
  const inviteOpenBtn = overlay.querySelector("[data-invite-open]") as HTMLButtonElement;
  const inviteBlock = overlay.querySelector("[data-invite-block]") as HTMLElement;
  const inviteUrlInput = overlay.querySelector("[data-invite-url]") as HTMLInputElement;
  const inviteCopyBtn = overlay.querySelector("[data-invite-copy]") as HTMLButtonElement;
  const inviteShareBtn = overlay.querySelector("[data-invite-share]") as HTMLButtonElement;
  const inviteWa = overlay.querySelector("[data-invite-wa]") as HTMLAnchorElement;
  // El menu de compartir del sistema solo donde existe (movil, Safari, Edge).
  inviteShareBtn.hidden = typeof (navigator as Navigator & { share?: unknown }).share !== "function";
  const createSubmitBtn = overlay.querySelector("[data-create-submit]") as HTMLButtonElement;
  const connPill = overlay.querySelector("[data-conn-pill]") as HTMLElement;
  const connLabel = overlay.querySelector("[data-conn-label]") as HTMLElement;
  const visibilityBtns = overlay.querySelectorAll("[data-visibility]") as NodeListOf<HTMLButtonElement>;
  /*
   * La jornada vive AQUI y no en el menu principal: es online, y ponerla en la
   * portada la separaba del unico sitio donde se puede jugar. Al pulsarla hace
   * lo mismo que hacia antes — apuntar la cola al modo de la semana y buscar —
   * solo que ya estas donde tiene que pasar.
   */
  const weeklyCard = overlay.querySelector("[data-weekly]") as HTMLButtonElement;
  const weeklyKicker = overlay.querySelector("[data-weekly-kicker]") as HTMLElement;
  const weeklyTitle = overlay.querySelector("[data-weekly-title]") as HTMLElement;
  const weeklyBlurb = overlay.querySelector("[data-weekly-blurb]") as HTMLElement;
  const weeklyClock = overlay.querySelector("[data-weekly-clock]") as HTMLElement;
  let weeklyTimer = 0;

  /** "2 d 4 h", "1 h 23 min", "8 min": solo las dos unidades que importan. */
  const humanGap = (ms: number): string => {
    const total = Math.max(0, Math.round(ms / 1000));
    const d = Math.floor(total / 86_400);
    const h = Math.floor((total % 86_400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (d > 0) return t("{d} d {h} h", { d, h });
    if (h > 0) return t("{h} h {m} min", { h, m });
    return t("{m} min", { m: Math.max(1, m) });
  };

  const syncWeekly = (): void => {
    const status = weeklyStatus();
    // La regla vive en shared, que no puede importar el i18n del cliente:
    // igual que `mode.blurb`, se traduce aqui al pintarla.
    weeklyTitle.textContent = t(status.rule.label);
    weeklyBlurb.textContent = t(status.rule.blurb);
    weeklyCard.classList.toggle("is-live", status.live);
    weeklyCard.disabled = !status.live;
    if (status.live) {
      weeklyKicker.textContent = t("La jornada · EN JUEGO");
      weeklyClock.textContent = t("Termina en {gap}", { gap: humanGap(status.endsInMs) });
    } else {
      weeklyKicker.textContent = t("La jornada");
      weeklyClock.textContent = t("Empieza en {gap}", { gap: humanGap(status.startsInMs) });
    }
  };

  const quickPanel = overlay.querySelector("[data-quick]") as HTMLElement;
  const quickModes = overlay.querySelector("[data-quick-modes]") as HTMLElement;
  const quickStatus = overlay.querySelector("[data-quick-status]") as HTMLElement;
  const quickBtn = overlay.querySelector("[data-quick-btn]") as HTMLButtonElement;
  const pulseBar = overlay.querySelector("[data-pulse]") as HTMLElement;
  const pulsePlaying = overlay.querySelector("[data-pulse-playing]") as HTMLElement;
  const pulseOnline = overlay.querySelector("[data-pulse-online]") as HTMLElement;
  const pulseRooms = overlay.querySelector("[data-pulse-rooms]") as HTMLElement;
  const roomCount = overlay.querySelector("[data-room-count]") as HTMLElement;
  const roomSearch = overlay.querySelector("[data-room-search]") as HTMLInputElement;
  const roomFilters = overlay.querySelector("[data-room-filters]") as HTMLElement;
  const modeFilter = overlay.querySelector("[data-mode-filter]") as HTMLSelectElement;
  const fieldGrid = overlay.querySelector("[data-field-grid]") as HTMLElement;
  const fieldHint = overlay.querySelector("[data-field-hint]") as HTMLElement;

  identityName.textContent = nick();
  (overlay.querySelector("[data-identity]") as HTMLElement).addEventListener("click", () => {
    void editNickname();
  });
  const unsubNick = onNicknameChange(() => {
    identityName.textContent = nick();
  });

  let selectedMode: OnlineModeId = "3v3";
  let quickMode: OnlineModeId = "3v3";
  /** Se ha pulsado JUGAR y aun no ha llegado el `match:start`. */
  let searching = false;
  let selectedVisibility: RoomVisibility = "public";
  /** Formato de la sala. 5 minutos y un tiempo es lo de siempre del juego. */
  let selectedMinutes = 5;
  let selectedPeriods = 1;
  let selectedDraw: MatchDrawRule = "golden";
  let selectedField: OnlineFieldId = DEFAULT_ONLINE_FIELD;
  let passwordResolve: ((pw: string | null) => void) | null = null;
  /** Ultima lista recibida, para poder refiltrar sin pedirla otra vez. */
  let lastRooms: OnlineRoom[] = [];
  let roomQuery = "";
  let roomFilter: "all" | "open" | "live" | "friends" = "all";
  let modeQuery: OnlineModeId | "" = "";

  for (const id of ONLINE_MODE_IDS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `lobby-mode-chip${id === selectedMode ? " is-active" : ""}`;
    btn.dataset.modeId = id;
    btn.innerHTML = `
      <span class="lobby-mode-chip-label">${MATCH_MODES[id].label}</span>
      <span class="lobby-mode-chip-meta">${t("{n} jug.", { n: maxHumanSlots(id) })}</span>
    `;
    btn.addEventListener("click", () => {
      selectedMode = id;
      modeGrid.querySelectorAll(".lobby-mode-chip").forEach((el) => {
        el.classList.toggle("is-active", (el as HTMLElement).dataset.modeId === id);
      });
    });
    modeGrid.appendChild(btn);
  }

  if (client) {
    quickPanel.hidden = false;
    for (const id of ONLINE_MODE_IDS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `lobby-mode-chip lobby-mode-chip--quick${id === quickMode ? " is-active" : ""}`;
      btn.dataset.quickModeId = id;
      btn.innerHTML = `
        <span class="lobby-mode-chip-label">${MATCH_MODES[id].label}</span>
        <span class="lobby-mode-chip-meta">${t("{n} jug.", { n: maxHumanSlots(id) })}</span>
      `;
      btn.addEventListener("click", () => selectQuickMode(id));
      quickModes.appendChild(btn);
    }

    weeklyCard.hidden = !SHOW_WEEKLY;
    weeklyCard.addEventListener("click", () => {
      if (weeklyCard.disabled || searching) return;
      selectQuickMode(weeklyStatus().rule.modeId);
      startQuickplay();
    });

    quickBtn.addEventListener("click", () => {
      if (searching) {
        cancelQuickplay();
        return;
      }
      startQuickplay();
    });
  }

  /** "2 x 8 min = 16 min": el total es lo que la gente quiere saber. */
  const syncTotal = (): void => {
    const hint = overlay.querySelector("[data-total-hint]");
    if (!hint) return;
    hint.textContent =
      selectedPeriods > 1
        ? t("{p} x {m} min · {total} min de juego", {
            p: selectedPeriods,
            m: selectedMinutes,
            total: selectedPeriods * selectedMinutes,
          })
        : "";
  };

  /**
   * Las pestañas del modal de crear sala.
   *
   * Se resetea a la primera cada vez que se ABRE: dejarla donde la dejaste la
   * vez anterior hace que el modal se abra por "Campo" y parezca otra cosa.
   */
  const showCreateTab = (name: string): void => {
    for (const tab of overlay.querySelectorAll<HTMLElement>("[data-ctab]")) {
      const on = tab.dataset.ctab === name;
      tab.classList.toggle("is-active", on);
      tab.setAttribute("aria-selected", on ? "true" : "false");
    }
    for (const pane of overlay.querySelectorAll<HTMLElement>("[data-cpane]")) {
      pane.classList.toggle("is-hidden", pane.dataset.cpane !== name);
    }
  };

  for (const tab of overlay.querySelectorAll<HTMLButtonElement>("[data-ctab]")) {
    tab.addEventListener("click", () => showCreateTab(tab.dataset.ctab ?? "sala"));
  }

  for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-minutes]")) {
    btn.addEventListener("click", () => {
      selectedMinutes = Number(btn.dataset.minutes);
      for (const b of overlay.querySelectorAll<HTMLElement>("[data-minutes]")) {
        b.classList.toggle("is-active", b === btn);
      }
      syncTotal();
    });
  }

  for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-periods]")) {
    btn.addEventListener("click", () => {
      selectedPeriods = Number(btn.dataset.periods);
      for (const b of overlay.querySelectorAll<HTMLElement>("[data-periods]")) {
        b.classList.toggle("is-active", b === btn);
      }
      syncTotal();
    });
  }

  for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-draw]")) {
    btn.addEventListener("click", () => {
      selectedDraw = btn.dataset.draw === "draw" ? "draw" : "golden";
      for (const b of overlay.querySelectorAll<HTMLElement>("[data-draw]")) {
        b.classList.toggle("is-active", b === btn);
      }
    });
  }

  visibilityBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      selectedVisibility = btn.dataset.visibility as RoomVisibility;
      visibilityBtns.forEach((b) => b.classList.toggle("is-active", b === btn));
    });
  });

  let resolvePick: ((r: OnlinePlayRequest | { back: true }) => void) | null = null;
  let activeRoomId: string | null = null;
  let unsub: (() => void) | null = null;
  let unsubClient: (() => void) | null = null;
  let unsubState: (() => void) | null = null;
  let awaitingCreate = false;
  /** Sala de un enlace cuya llegada abre el modal. */
  let awaitingJoinId: string | null = null;
  let pendingCreateName: string | null = null;
  let connectionState: ConnectionState = client?.state ?? "online";
  let refreshTimer = 0;
  let lobbyToastTimer = 0;

  // ------------------------------------------------------------ partida rapida

  // ------------------------------------------------------------- buscar salas

  const refilter = (): void => renderList(lastRooms);

  roomSearch.addEventListener("input", () => {
    roomQuery = roomSearch.value;
    refilter();
  });

  for (const btn of roomFilters.querySelectorAll<HTMLButtonElement>(".lobby-filter")) {
    btn.addEventListener("click", () => {
      roomFilter = (btn.dataset.filter as typeof roomFilter) ?? "all";
      for (const el of roomFilters.querySelectorAll<HTMLElement>(".lobby-filter")) {
        el.classList.toggle("is-active", el === btn);
      }
      refilter();
    });
  }

  for (const id of ONLINE_MODE_IDS) {
    const opt = document.createElement("option");
    opt.value = id;
    opt.textContent = t("{modo} · {n} jug.", { modo: MATCH_MODES[id].label, n: maxHumanSlots(id) });
    modeFilter.appendChild(opt);
  }
  modeFilter.addEventListener("change", () => {
    modeQuery = (modeFilter.value as OnlineModeId | "") || "";
    refilter();
  });

  // ---------------------------------------------------------- campo de la sala

  const setField = (id: OnlineFieldId): void => {
    selectedField = id;
    fieldHint.textContent = t(PITCH_SKINS[id].tagline);
    for (const btn of fieldGrid.querySelectorAll<HTMLElement>(".lobby-pitch")) {
      const on = btn.dataset.field === id;
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-checked", on ? "true" : "false");
    }
  };

  for (const id of PITCH_SKIN_IDS) {
    const skin = PITCH_SKINS[id];
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "lobby-pitch";
    btn.dataset.field = id;
    btn.setAttribute("role", "radio");
    btn.style.cssText = [
      `--pitch-a:#${skin.stripeA.toString(16).padStart(6, "0")}`,
      `--pitch-b:#${skin.stripeB.toString(16).padStart(6, "0")}`,
      `--pitch-line:#${skin.line.toString(16).padStart(6, "0")}`,
    ].join(";");
    btn.innerHTML = `
      <span class="lobby-pitch-swatch" aria-hidden="true"></span>
      <span class="lobby-pitch-label">${t(skin.label)}</span>
    `;
    btn.addEventListener("click", () => setField(id));
    fieldGrid.appendChild(btn);
  }
  setField(selectedField);

  // ------------------------------------------------------- pulso del servidor

  /**
   * El contador sube y baja CONTANDO, no saltando.
   *
   * Un numero que cambia de 12 a 31 de golpe se lee como un dato refrescado; el
   * mismo numero pasando por 13, 14, 15… se lee como gente entrando. Es la
   * diferencia entre una cifra y una señal de vida, y no cuesta nada.
   */
  const rollTo = (el: HTMLElement, target: number): void => {
    const from = Number(el.textContent) || 0;
    if (from === target) return;
    const step = from < target ? 1 : -1;
    const ticks = Math.min(Math.abs(target - from), 24);
    let current = from;
    let done = 0;
    el.classList.add("is-rolling");
    const timer = window.setInterval(() => {
      done += 1;
      current = done >= ticks ? target : current + step * Math.ceil(Math.abs(target - from) / ticks);
      el.textContent = String(current);
      if (done >= ticks) {
        window.clearInterval(timer);
        el.textContent = String(target);
        el.classList.remove("is-rolling");
      }
    }, 40);
  };

  const applyPresence = (presence: OnlinePresence): void => {
    pulseBar.classList.toggle("is-alive", presence.online > 0);
    rollTo(pulsePlaying, presence.playing);
    rollTo(pulseOnline, presence.online);
    rollTo(pulseRooms, presence.rooms);
  };

  /**
   * Ya no hay cola que contar.
   *
   * Esto enseñaba "Buscando… 1/6 jugadores · empieza en 18 s con bots", que era
   * la verdad de un sistema que te hacia esperar. Ahora se pulsa y se entra, asi
   * que lo unico que queda es el parpadeo de "entrando" hasta que llega el
   * `match:start` — y si tarda mas de lo normal, la salida para cancelar.
   */
  const renderQuickStatus = (): void => {
    const party = options.party;
    const withParty = party?.inParty() ?? false;
    const leads = withParty && (party?.isLeader() ?? false);
    if (!searching) {
      // Con grupo: el lider lleva a todos; los demas van donde vaya el.
      const leaderName = party?.state()?.members.find((m) => m.clientId === party.state()?.leaderId)?.nickname ?? "";
      quickStatus.textContent = withParty && !leads ? t("Vas con el grupo de {nick}: donde entre, vas detrás", { nick: leaderName }) : "";
      quickBtn.textContent = leads ? t("JUGAR CON TU GRUPO") : t("JUGAR YA");
      quickBtn.classList.remove("is-searching");
      quickModes.classList.remove("is-locked");
      return;
    }
    quickBtn.textContent = t("Cancelar");
    quickBtn.classList.add("is-searching");
    // El modo no se cambia a medio entrar: seria salir de un partido y entrar
    // en otro sin que se note.
    quickModes.classList.add("is-locked");
    // Con grupo, la sala espera a que entren todos antes de arrancar.
    quickStatus.textContent = leads ? t("Esperando a tu grupo…") : t("Entrando…");
  };

  const partyChip = overlay.querySelector("[data-lobby-party]") as HTMLButtonElement;
  const renderPartyChip = (): void => {
    const party = options.party;
    const view = party?.state();
    if (!party || !view || view.members.length < 2) {
      partyChip.hidden = true;
      return;
    }
    partyChip.hidden = false;
    partyChip.title = view.members.map((m) => (m.clientId === view.leaderId ? `👑 ${m.nickname}` : m.nickname)).join(" · ");
    partyChip.setAttribute("aria-label", t("Tu grupo: {names}", { names: view.members.map((m) => m.nickname).join(", ") }));
    const label = document.createElement("span");
    label.className = "lobby-party-label";
    label.textContent = `${t("GRUPO")} ${view.members.length}/${view.max}`;
    const faces = document.createElement("span");
    faces.className = "hub-squad";
    for (const m of view.members.slice(0, 4)) {
      const i = document.createElement("i");
      i.className = `hub-squad-face${m.clientId === view.leaderId ? " is-lead" : ""}${m.status === "away" ? " is-away" : ""}`;
      i.textContent = (m.nickname.trim()[0] ?? "?").toUpperCase();
      i.style.setProperty("--face", `hsl(${[...m.nickname].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7)} 55% 42%)`);
      faces.appendChild(i);
    }
    partyChip.replaceChildren(faces, label);
  };
  partyChip.addEventListener("click", () => options.openSocial?.());
  const unsubParty =
    options.party?.subscribe(() => {
      renderPartyChip();
      renderQuickStatus();
    }) ?? null;

  const setSearching = (next: boolean): void => {
    searching = next;
    renderQuickStatus();
  };

  const selectQuickMode = (id: OnlineModeId): void => {
    if (searching) return;
    quickMode = id;
    quickModes.querySelectorAll(".lobby-mode-chip").forEach((el) => {
      el.classList.toggle("is-active", (el as HTMLElement).dataset.quickModeId === id);
    });
  };

  const startQuickplay = (): void => {
    if (!client || connectionState !== "online" || searching) return;
    // Entrar en la cola deja cualquier sala en la que estuvieras: el server
    // hace lo mismo, y dejarla abierta aqui confunde el modal.
    if (activeRoomId) {
      modal.classList.add("is-hidden");
      activeRoomId = null;
    }
    setSearching(true);
    client.send({ type: "quickplay", modeId: quickMode, nickname: nick() });
  };

  const cancelQuickplay = (): void => {
    if (!searching) return;
    client?.send({ type: "quickplay:cancel" });
    setSearching(false);
  };

  // ------------------------------------------------------------ invitaciones

  /**
   * El enlace que se pega en el grupo. El id de la sala ES el secreto, y va
   * con tu codigo de amigo: quien lo abra y entre sera tu amigo sin mas.
   */
  const inviteUrlFor = (roomId: string): string => roomInviteUrl(roomId, friendsStore?.state()?.code);
  const inviteTextFor = (room: OnlineRoom): string => roomInviteText(room.modeId, inviteUrlFor(room.id));

  let copyResetTimer = 0;

  const copyInvite = async (): Promise<void> => {
    const url = inviteUrlInput.value;
    if (!url) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      // Sin permiso de portapapeles (o sin https): al menos se deja
      // seleccionado para que un Ctrl+C manual funcione.
      inviteUrlInput.focus();
      inviteUrlInput.select();
    }
    inviteCopyBtn.textContent = ok ? t("¡Copiado!") : t("Cópialo tú");
    inviteCopyBtn.classList.add("is-copied");
    clearTimeout(copyResetTimer);
    copyResetTimer = window.setTimeout(() => {
      inviteCopyBtn.textContent = t("Copiar");
      inviteCopyBtn.classList.remove("is-copied");
    }, 1800);
  };

  const shareInvite = (): void => {
    if (!shownRoom || shownRoom.id.startsWith("pending-")) return;
    // Sin esperas antes: Safari solo abre el menu dentro del propio toque.
    void shareText(inviteTextFor(shownRoom)).then((outcome) => {
      if (outcome === "copied") showLobbyToast(t("Enlace copiado. Pégalo en el grupo"));
    });
  };

  const syncInvite = (room: OnlineRoom): void => {
    const isLink = room.visibility === "link";
    inviteBlock.classList.toggle("is-hidden", !isLink);
    if (!isLink) return;

    // La sala se pinta de forma optimista antes de que el server conteste, y su
    // id provisional NO sirve: quien reciba ese enlace no llegaria a ninguna
    // parte. Hasta que llega el id de verdad, no hay nada que copiar.
    const pending = room.id.startsWith("pending-");
    inviteUrlInput.value = pending ? t("Creando la sala…") : inviteUrlFor(room.id);
    inviteUrlInput.readOnly = true;
    inviteUrlInput.classList.toggle("is-pending", pending);
    inviteCopyBtn.disabled = pending;
    inviteShareBtn.disabled = pending;
    inviteWa.classList.toggle("is-disabled", pending);
    if (pending) inviteWa.removeAttribute("href");
    else inviteWa.href = whatsappUrl(inviteTextFor(room));
  };

  /**
   * Aviso flotante. Con `action`, lleva un boton ("Unirse", "Ver"): una
   * invitacion que solo se puede leer obliga a ir a buscar la sala a mano.
   */
  const showLobbyToast = (text: string, action?: { label: string; run: () => void }, tone: "info" | "error" = "info"): void => {
    let toast = overlay.querySelector(".lobby-toast") as HTMLElement | null;
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "lobby-toast";
      toast.setAttribute("role", "status");
      overlay.appendChild(toast);
    }
    toast.replaceChildren();
    toast.dataset.tone = tone;
    const msg = document.createElement("span");
    msg.textContent = text;
    toast.appendChild(msg);
    if (action) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "lobby-toast-action";
      btn.textContent = action.label;
      btn.addEventListener("click", () => {
        toast?.classList.remove("is-visible");
        action.run();
      });
      toast.appendChild(btn);
    }
    toast.classList.add("is-visible");
    clearTimeout(lobbyToastTimer);
    lobbyToastTimer = window.setTimeout(() => toast?.classList.remove("is-visible"), action ? 9000 : 4000);
  };

  const closePasswordModal = (value: string | null): void => {
    passwordModal.classList.add("is-hidden");
    passwordModal.setAttribute("aria-hidden", "true");
    passwordInput.value = "";
    const done = passwordResolve;
    passwordResolve = null;
    done?.(value);
  };

  const askPassword = (title: string, kicker: string): Promise<string | null> =>
    new Promise((resolve) => {
      passwordResolve = resolve;
      passwordTitle.textContent = title;
      passwordKicker.textContent = kicker;
      passwordModal.classList.remove("is-hidden");
      passwordModal.setAttribute("aria-hidden", "false");
      passwordInput.focus();
    });

  const openCreateModal = (): void => {
    createModal.classList.remove("is-hidden");
    // Siempre por la primera: abrir por donde lo dejaste parece otro modal.
    showCreateTab("sala");
    createModal.setAttribute("aria-hidden", "false");
    roomNameInput.value = "";
    roomNameInput.focus();
  };

  const closeCreateModal = (): void => {
    createModal.classList.add("is-hidden");
    createModal.setAttribute("aria-hidden", "true");
  };

  const ownedLobbyRoom = (rooms: OnlineRoom[]): OnlineRoom | null => {
    const owned = rooms.filter((r) => r.hostId === store.clientId && r.status === "lobby");
    if (owned.length === 0) return null;
    if (pendingCreateName) {
      const named = owned.find((r) => r.name === pendingCreateName);
      if (named) return named;
    }
    return owned.sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;
  };

  const openOwnedCreate = (rooms: OnlineRoom[]): void => {
    const created = ownedLobbyRoom(rooms);
    if (!created) return;
    awaitingCreate = false;
    pendingCreateName = null;
    closeCreateModal();
    activeRoomId = created.id;
    openModal(created);
  };

  const startRefresh = (): void => {
    clearInterval(refreshTimer);
    refreshTimer = window.setInterval(() => store.refresh(), 4000);
  };

  const stopRefresh = (): void => {
    clearInterval(refreshTimer);
    refreshTimer = 0;
  };

  const setConnectionUi = (state: ConnectionState): void => {
    connectionState = state;
    connLabel.textContent = connectionLabel(state);
    connPill.dataset.state = state;
    const online = state === "online";
    createOpenBtn.disabled = !online;
    createSubmitBtn.disabled = !online;
    inviteOpenBtn.disabled = !online;
    quickBtn.disabled = !online;
    if (!online && searching) setSearching(false);
    roomList.querySelectorAll(".room-row").forEach((btn) => {
      (btn as HTMLButtonElement).disabled = !online;
    });
  };

  if (client) {
    unsubState = client.onState(setConnectionUi);
    unsubClient = client.onMessage((msg) => {
      if (msg.type === "presence") {
        applyPresence(msg.presence);
        return;
      }
      // El apodo que deja el servidor lo guarda `online/nickname.ts`, y el
      // lobby se entera por `onNicknameChange`.
      if (msg.type === "error") {
        if (msg.code === "quickplay_failed") setSearching(false);
        if (msg.code === "create_failed") {
          awaitingCreate = false;
          pendingCreateName = null;
        }
        if (
          msg.code === "bad_password" ||
          msg.code === "too_many_attempts" ||
          msg.code === "banned" ||
          msg.code === "room_full" ||
          // El de "el anfitrion no responde" te deja en el banquillo: no se
          // cierra la sala, solo se avisa.
          (msg.code === "join_failed" && !store.get(activeRoomId ?? "")?.bench?.some((b) => b.clientId === store.clientId)) ||
          msg.code === "match_closed"
        ) {
          // Un enlace caducado deja el modal esperando a una sala que ya no existe.
          activeRoomId = null;
          awaitingJoinId = null;
          modal.classList.add("is-hidden");
        }
        showLobbyToast(msg.message, undefined, "error");
        return;
      }
      if (msg.type === "room:kicked") {
        if (activeRoomId === msg.roomId) {
          activeRoomId = null;
          awaitingJoinId = null;
          modal.classList.add("is-hidden");
        }
        showLobbyToast(t("Te han expulsado de la sala"));
        return;
      }
      if (msg.type === "room:update" && msg.room.hostId === store.clientId && awaitingCreate) {
        openOwnedCreate([msg.room, ...store.list()]);
        return;
      }
      if (msg.type === "match:latejoin") {
        const localSeat = msg.playerId;
        finish({
          room: {
            id: msg.roomId,
            name: store.get(msg.roomId)?.name ?? msg.roomId,
            modeId: msg.modeId,
            hostId: store.get(msg.roomId)?.hostId ?? "",
            seats: msg.seats,
            status: "playing",
            createdAt: store.get(msg.roomId)?.createdAt ?? Date.now(),
            visibility: store.get(msg.roomId)?.visibility ?? "public",
            requiresPassword: store.get(msg.roomId)?.requiresPassword,
            // Formato y campo del MENSAJE, no de la lista: quien entra por un
            // enlace puede no tener la sala en su lista, y otro formato es otro
            // partido. Antes aqui iba el campo por defecto y ningun formato.
            fieldId: msg.fieldId ?? DEFAULT_ONLINE_FIELD,
            minutes: msg.minutes,
            periods: msg.periods,
            onDraw: msg.onDraw,
          },
          nickname: nick(),
          localPlayerId: localSeat,
          seed: msg.seed,
          roomId: msg.roomId,
          client,
          lateJoinHistory: msg.history,
          // Sin esto, en una sala de jornada el que entraba jugaba con las
          // reglas normales y los demas con las de la jornada.
          weeklyRuleId: msg.weeklyRuleId,
        });
        return;
      }
      if (msg.type !== "match:start") return;
      const localSeat = Object.entries(msg.seats).find(([, o]) => o.clientId === store.clientId);
      if (!localSeat) return;
      finish({
        room: {
          id: msg.roomId,
          name: store.get(msg.roomId)?.name ?? msg.roomId,
          modeId: msg.modeId,
          hostId: store.get(msg.roomId)?.hostId ?? "",
          seats: msg.seats,
          status: "playing",
          createdAt: Date.now(),
          visibility: store.get(msg.roomId)?.visibility ?? "public",
          fieldId: msg.fieldId ?? store.get(msg.roomId)?.fieldId ?? DEFAULT_ONLINE_FIELD,
          // El formato que eligio quien abrio la sala. No se copiaba, asi que
          // en online se jugaba siempre el de fabrica elijas lo que elijas.
          minutes: msg.minutes,
          periods: msg.periods,
          onDraw: msg.onDraw,
        },
        nickname: nick(),
        localPlayerId: localSeat[0],
        seed: msg.seed,
        roomId: msg.roomId,
        client,
        weeklyRuleId: msg.weeklyRuleId,
        solo: msg.solo,
      });
    });
  } else {
    setConnectionUi("online");
  }

  const hide = (): void => {
    // Fuera del lobby, fuera de su chat: a quien juega no le interesa.
    lobbyChat?.leave();
    closePersonMenu?.();
    overlay.classList.add("is-hidden");
    modal.classList.add("is-hidden");
    closeCreateModal();
    closePasswordModal(null);
    stopRefresh();
    activeRoomId = null;
    // Fuera del lobby no se esta leyendo nada: lo que llegue, sin leer.
    dms?.close();
  };

  const finish = (result: OnlinePlayRequest | { back: true }): void => {
    if (!resolvePick) return;
    // Volver al menu con la cola puesta dejaria el asiento ocupado por un
    // jugador que ya no esta mirando.
    if ("back" in result) cancelQuickplay();
    else setSearching(false);
    const done = resolvePick;
    resolvePick = null;
    activeRoomId = null;
    closeCreateModal();
    closePasswordModal(null);
    stopRefresh();
    hide();
    done(result);
  };

  /**
   * El nombre vive en `online/nickname.ts`. Se cambia pulsando tu nombre en la
   * barra de arriba (o en personalizar): es unico y lo confirma el servidor.
   */
  function nick(): string {
    return clampNick(loadNickname()) || t("Jugador");
  }

  const submitCreate = async (): Promise<void> => {
    if (connectionState !== "online") return;

    let password: string | undefined;
    if (selectedVisibility === "private") {
      closeCreateModal();
      const pw = await askPassword(t("Contraseña de la sala"), t("Sala privada"));
      if (!pw?.trim()) return;
      password = pw.trim();
    }

    if (activeRoomId) {
      store.leave(activeRoomId);
      modal.classList.add("is-hidden");
      activeRoomId = null;
    }
    const name = roomNameInput.value.trim() || t("Sala {modo}", { modo: selectedMode });
    awaitingCreate = true;
    pendingCreateName = name;
    const created = store.create({
      name,
      modeId: selectedMode,
      nickname: nick(),
      visibility: selectedVisibility,
      fieldId: selectedField,
      password,
      minutes: selectedMinutes,
      periods: selectedPeriods,
      onDraw: selectedDraw,
    });
    closeCreateModal();
    activeRoomId = created.id;
    openModal(created);
  };

  const createInviteRoom = (): void => {
    if (connectionState !== "online") return;
    if (activeRoomId) {
      store.leave(activeRoomId);
      modal.classList.add("is-hidden");
      activeRoomId = null;
    }
    awaitingCreate = true;
    pendingCreateName = t("Con amigos");
    const created = store.create({
      name: t("Con amigos"),
      modeId: selectedMode,
      nickname: nick(),
      visibility: "link",
      fieldId: selectedField,
    });
    activeRoomId = created.id;
    openModal(created);
  };

  /**
   * Jugar con un amigo en un toque. Con sala (de verdad, no la provisional),
   * se le invita a ella. Sin sala, se monta una "Con amigos" y se le invita en
   * cuanto el servidor da su id: invitar a la provisional no llegaria a nadie.
   */
  const pendingPlayWith = new Set<string>();
  const playWith = (clientId: string): void => {
    if (connectionState !== "online") {
      showLobbyToast(t("Sin conexión con el servidor"), undefined, "error");
      return;
    }
    if (activeRoomId && !activeRoomId.startsWith("pending-")) {
      friendsStore?.invite(activeRoomId, clientId);
      invited.add(`${activeRoomId}:${clientId}`);
      if (shownRoom) syncInviteFriends(shownRoom);
      return;
    }
    pendingPlayWith.add(clientId);
    if (!activeRoomId) createInviteRoom();
  };
  const flushPlayWith = (): void => {
    if (pendingPlayWith.size === 0 || !activeRoomId || activeRoomId.startsWith("pending-")) return;
    for (const id of pendingPlayWith) {
      friendsStore?.invite(activeRoomId, id);
      invited.add(`${activeRoomId}:${id}`);
    }
    pendingPlayWith.clear();
  };
  /** Sala para pasar el enlace fuera: la tuya si ya es de enlace, o una nueva. */
  const inviteNew = (): void => {
    if (connectionState !== "online") {
      showLobbyToast(t("Sin conexión con el servidor"), undefined, "error");
      return;
    }
    const current = activeRoomId ? store.get(activeRoomId) : null;
    if (!current || current.visibility !== "link") createInviteRoom();
    inviteBlock.classList.add("is-spotlight");
    window.setTimeout(() => inviteBlock.classList.remove("is-spotlight"), 2400);
  };

  const joinById = (roomId: string): void => {
    if (connectionState !== "online") {
      showLobbyToast("Sin conexión con el servidor");
      return;
    }
    activeRoomId = roomId;
    // La sala aun no esta en el store: se abre el modal en cuanto llegue.
    awaitingJoinId = roomId;
    store.join(roomId, nick());
  };

  const tryJoinRoom = async (room: OnlineRoom): Promise<void> => {
    let password: string | undefined;
    if (room.requiresPassword || roomVisibility(room) === "private") {
      const pw = await askPassword(t("Entrar en {sala}", { sala: room.name }), t("Contraseña requerida"));
      if (!pw?.trim()) return;
      password = pw.trim();
    }
    activeRoomId = room.id;
    // Se abre la sala tambien con el partido en marcha: si no hay sitio en el
    // partido, entras al banquillo y es aqui donde se espera. Si si lo hay,
    // llega el `match:latejoin` y el lobby se cierra solo.
    awaitingJoinId = room.id;
    store.join(room.id, nick(), password);
  };

  /**
   * Orden de la lista, y no es cosmetico.
   *
   * Con cuatro salas da igual; con cuarenta, lo primero que se ve ES la lista.
   * El criterio va de "puedo entrar ya y va a arrancar pronto" hacia abajo:
   *
   *  1. Las tuyas, que es lo que venias a buscar.
   *  2. Las que esperan gente antes que las que ya juegan: entrar a una partida
   *     empezada es el plan B.
   *  3. Entre las que esperan, la MAS LLENA primero — es la que va a arrancar
   *     antes, y repartir a la gente por salas vacias es como no arranca nunca.
   *  4. Las cerradas al final, que no se pueden ni tocar.
   */
  /** Amigos que estan ahora en cada sala: lo que mas pesa para elegir. */
  const friendsIn = (roomId: string): string[] =>
    (friendsStore?.state()?.friends ?? []).filter((f) => f.roomId === roomId).map((f) => f.nickname);

  const roomRank = (room: OnlineRoom): number => {
    if (room.hostId === store.clientId) return 0;
    // Donde estan tus amigos va justo detras de lo tuyo: es a donde vas.
    if (friendsIn(room.id).length > 0) return 1;
    if (room.full) return 5;
    if (room.joinable === false) return 4;
    return room.status === "playing" ? 3 : 2;
  };

  const visibleRooms = (rooms: OnlineRoom[]): OnlineRoom[] => {
    const q = roomQuery.trim().toLowerCase();
    return rooms
      .filter((r) => r.status === "lobby" || r.status === "playing")
      .filter((r) => (q ? r.name.toLowerCase().includes(q) : true))
      .filter((r) => (modeQuery ? r.modeId === modeQuery : true))
      .filter((r) => {
        if (roomFilter === "live") return r.status === "playing";
        if (roomFilter === "friends") return friendsIn(r.id).length > 0;
        if (roomFilter === "open") {
          return r.joinable !== false && occupiedCount(r) < maxHumanSlots(r.modeId);
        }
        return true;
      })
      .sort(
        (a, b) =>
          roomRank(a) - roomRank(b) ||
          occupiedCount(b) - occupiedCount(a) ||
          b.createdAt - a.createdAt,
      );
  };

  const renderList = (rooms: OnlineRoom[]): void => {
    lastRooms = rooms;
    roomList.innerHTML = "";
    const open = visibleRooms(rooms);
    const total = rooms.filter((r) => r.status === "lobby" || r.status === "playing").length;
    roomCount.textContent = total > 0 ? String(total) : "";

    if (open.length === 0) {
      // Dos vacios distintos: "no hay nada" y "tus filtros no dejan ver nada".
      // Decirle a alguien que cree una sala cuando lo que pasa es que ha escrito
      // mal el nombre es mandarlo por el camino largo.
      const filtered = total > 0;
      roomList.innerHTML = filtered
        ? `
        <div class="room-empty-state">
          <p class="room-empty-title">${t("Ninguna sala encaja")}</p>
          <p class="room-empty">${t("Hay {n} salas abiertas. Prueba a quitar filtros.", { n: total })}</p>
        </div>`
        : `
        <div class="room-empty-state">
          <p class="room-empty-title">${t("No hay salas abiertas")}</p>
          <p class="room-empty">${t("Crea una sala y pulsa")} <strong>${t("Jugar")}</strong>${t(": los huecos vacíos son bots. Quien entre ocupará un bot al azar.")}</p>
        </div>`;
      return;
    }

    for (const room of open) {
      const max = maxHumanSlots(room.modeId);
      const n = occupiedCount(room);
      // "Tuya" = eres el anfitrion; "dentro" = estas en ella, jugando o
      // esperando. A una sala en la que ya estas no se vuelve a pedir entrar:
      // se abre.
      const isOwn = room.hostId === store.clientId;
      const inside =
        isOwn ||
        Object.values(room.seats).some((o) => o?.clientId === store.clientId) ||
        (room.bench ?? []).some((b) => b.clientId === store.clientId);
      const vis = roomVisibility(room);
      const playing = room.status === "playing";
      // El server cierra la entrada cuando el partido va tan avanzado que
      // reproducirlo desde cero costaria megas de historial.
      // Al PARTIDO ya no se entra (va tan avanzado que rehacerlo costaria
      // megas), pero a la sala si: se espera en el banquillo al siguiente.
      const closed = room.joinable === false;
      const full = room.full === true;
      const row = document.createElement("button");
      row.type = "button";
      row.className = `room-row${isOwn ? " is-own" : ""}${closed || full ? " is-closed" : ""}`;
      row.role = "listitem";
      row.disabled = connectionState !== "online" || (full && !inside);
      const buddies = friendsIn(room.id);
      const who = whoIsIn(room, max, buddies);
      if (buddies.length > 0) row.classList.add("has-friends");
      const bench = room.benchCount ?? 0;
      const state = full
        ? `<span class="rr-state is-full">${t("Llena")}</span>`
        : playing
          ? `<span class="rr-state is-live">${t("En juego")} <small>${runningFor(room)}</small></span>`
          : `<span class="rr-state is-wait">${t("Esperando")}</span>`;
      const action = inside
        ? t("Abrir")
        : full
          ? "—"
          : closed
            ? t("Banquillo")
            : playing
              ? t("Unirse")
              : t("Entrar");
      /*
       * Una fila = una sala, en columnas que se escanean de arriba abajo:
       * nombre y quien hay, modo, huecos, estado y la accion. Con doscientas
       * salas nadie lee tarjetas; se recorren columnas buscando "3v3" o
       * "esperando", y los amigos saltan a la vista en dorado.
       */
      row.innerHTML = `
        <span class="rr-name">
          <span class="rr-title">
            <strong>${escapeHtml(room.name)}</strong>
            ${isOwn ? `<span class="room-badge room-badge--own">${t("tuya")}</span>` : ""}
            ${room.auto ? `<span class="room-badge room-badge--auto">${t("rápida")}</span>` : ""}
            ${vis !== "public" ? `<span class="room-badge room-badge--${vis}">${vis === "private" ? "🔒 " : ""}${VISIBILITY_LABEL[vis]}</span>` : ""}
          </span>
          <span class="rr-who">${
            buddies.length > 0
              ? `<span class="rr-friends">★ ${buddies.map(escapeHtml).join(", ")}</span>${who ? " · " : ""}`
              : ""
          }${who}</span>
        </span>
        <span class="rr-mode"><b>${room.modeId}</b><small>${room.minutes ?? 5} ${t("min")}</small></span>
        <span class="rr-players">
          <span class="room-pips" aria-hidden="true">${seatPips(n, max)}</span>
          <small>${n}/${max}${bench > 0 ? ` · ${t("{n} esperando", { n: bench })}` : ""}</small>
        </span>
        ${state}
        <span class="room-row-join">${action}</span>
      `;
      row.addEventListener("click", () => {
        if (full && !inside) return;
        if (inside) {
          // Volver a TU sala es volver a pedir la entrada: le dice al servidor
          // que ya no estas en los resultados y, si el partido sigue y aun se
          // puede, te devuelve a el con el historial; si no, al banquillo.
          openModal(room);
          joinById(room.id);
          return;
        }
        void tryJoinRoom(room);
      });
      roomList.appendChild(row);
    }
  };

  const openModal = (room: OnlineRoom): void => {
    activeRoomId = room.id;
    modal.classList.remove("is-hidden");
    syncModal(room);
  };

  /**
   * Cambio de bando: se delega en el contenedor porque los asientos se
   * repintan enteros en cada `room:update`, y volver a enganchar listeners
   * uno a uno en cada repintado es la forma clasica de fugarlos.
   */
  const onSeatClick = (event: Event): void => {
    const btn = (event.target as HTMLElement | null)?.closest("[data-seat]");
    if (!(btn instanceof HTMLElement) || !activeRoomId) return;
    const seatId = btn.dataset.seat;
    if (seatId) store.takeSeat(activeRoomId, seatId);
  };
  seatsHome.addEventListener("click", onSeatClick);
  seatsAway.addEventListener("click", onSeatClick);

  /**
   * Denunciar. Se delega igual que el cambio de asiento: los asientos se
   * repintan enteros en cada `room:update`.
   */
  const onReportClick = (event: Event): void => {
    const btn = (event.target as HTMLElement | null)?.closest("[data-report]");
    if (!(btn instanceof HTMLElement) || !activeRoomId) return;
    const playerId = btn.dataset.report;
    if (!playerId) return;
    event.stopPropagation();
    if (!client) return;
    client.send({ type: "player:report", roomId: activeRoomId, playerId });
  };
  seatsHome.addEventListener("click", onReportClick);
  seatsAway.addEventListener("click", onReportClick);

  const benchList = overlay.querySelector("[data-bench]") as HTMLElement;
  const benchEmpty = overlay.querySelector("[data-bench-empty]") as HTMLElement;
  const benchCount = overlay.querySelector("[data-bench-count]") as HTMLElement;
  const liveBanner = overlay.querySelector("[data-room-live]") as HTMLElement;
  const liveText = overlay.querySelector("[data-room-live-text]") as HTMLElement;
  const seatNote = overlay.querySelector("[data-seat-note]") as HTMLElement;
  const selfSeatBtn = overlay.querySelector("[data-self-seat]") as HTMLButtonElement;
  const chatLog = overlay.querySelector("[data-chat-log]") as HTMLOListElement;
  const chatForm = overlay.querySelector("[data-chat-form]") as HTMLFormElement;
  const chatInput = overlay.querySelector("[data-chat-input]") as HTMLInputElement;
  const chatAside = overlay.querySelector(".room-chat") as HTMLElement;

  /** Botoncito de una orden del anfitrion sobre una fila. */
  const actBtn = (act: string, clientId: string, label: string, title: string, danger = false): string =>
    `<button type="button" class="seat-act${danger ? " is-danger" : ""}" data-act="${act}" data-client="${escapeHtml(
      clientId,
    )}" title="${escapeHtml(title)}" aria-label="${escapeHtml(title)}">${label}</button>`;

  /** Sala que se esta pintando, para que los botones sepan de que va. */
  let shownRoom: OnlineRoom | null = null;

  const syncModal = (room: OnlineRoom): void => {
    shownRoom = room;
    const title = overlay.querySelector("[data-room-title]") as HTMLElement;
    const meta = overlay.querySelector("[data-room-meta]") as HTMLElement;
    const startBtn = overlay.querySelector("[data-start]") as HTMLButtonElement;
    const vis = roomVisibility(room);
    const me = store.clientId;
    const isHost = room.hostId === me;
    const playing = room.status === "playing";
    const online = connectionState === "online";
    const seatsOfMode = fieldSeatsForMode(room.modeId);
    const mySeat = Object.entries(room.seats).find(([, o]) => o?.clientId === me)?.[0] ?? null;
    const hostNick =
      Object.values(room.seats).find((o) => o?.clientId === room.hostId)?.nickname ??
      room.bench?.find((b) => b.clientId === room.hostId)?.nickname ??
      "";
    title.textContent = room.name;
    meta.textContent = isHost
      ? `${room.modeId} · ${VISIBILITY_LABEL[vis]} · ${t("eres el anfitrión")}`
      : `${room.modeId} · ${VISIBILITY_LABEL[vis]} · ${t("anfitrión: {nick}", { nick: hostNick || "—" })}`;

    // Un asiento por columna de equipo. Enfrentar los dos bloques es lo que
    // hace la sala legible de un vistazo y lo que la mantiene escalable: en
    // 7v7 son seis y seis, no una lista de doce donde hay que ir leyendo la
    // etiqueta LOCAL/VISIT de cada fila para saber quien juega con quien.
    const badge = overlay.querySelector("[data-room-badge]") as HTMLElement;
    badge.textContent = `${room.modeId} · ${VISIBILITY_LABEL[vis]}`;

    // Con el partido en marcha solo se entra si aun admite gente y hay hueco
    // en ese equipo. El servidor lo vuelve a comprobar; esto es para no
    // ofrecer botones que no hacen nada.
    const canEnter = !playing || room.joinable !== false;
    const freeOn = (side: "home" | "away"): boolean =>
      seatsOfMode.some((s) => s.side === side && !room.seats[s.playerId]);

    seatsHome.innerHTML = "";
    seatsAway.innerHTML = "";
    let humansHome = 0;
    let humansAway = 0;
    let indexHome = 0;
    let indexAway = 0;

    for (const seat of seatsOfMode) {
      const occ = room.seats[seat.playerId];
      const home = seat.side === "home";
      const isMe = occ?.clientId === me;
      const slot = home ? ++indexHome : ++indexAway;
      if (occ) {
        if (home) humansHome++;
        else humansAway++;
      }

      // Un hueco libre es un sitio al que te puedes cambiar: se pinta como
      // boton para que se note que se puede pulsar, no como fila muerta.
      const free = !occ && !playing;
      const li = document.createElement("li");
      li.className = ["seat", occ ? "is-human" : "is-bot", isMe ? "is-me" : "", free ? "is-free" : "", occ?.away ? "is-away" : ""]
        .filter(Boolean)
        .join(" ");
      const tag = isMe ? t("tú") : occ?.away ? t("en resultados") : occ ? (occ.clientId === room.hostId ? t("anfitrión") : t("humano")) : t("IA");
      const hostActs =
        occ && isHost && !isMe
          ? actBtn("bench", occ.clientId, "↓", t("Al banquillo")) + actBtn("kick", occ.clientId, "✕", t("Expulsar de la sala"), true)
          : "";
      const body =
        `<span class="seat-slot">${slot}</span>` +
        `<span class="seat-nick">${occ ? tierMarkHtml(occ.tier) + escapeHtml(occ.nickname) : t("Bot")}</span>` +
        `<span class="seat-tag">${tag}</span>` +
        (hostActs ? `<span class="seat-acts">${hostActs}</span>` : "") +
        // Solo sobre personas, y nunca sobre uno mismo.
        (occ && !isMe
          ? `<button type="button" class="seat-report" data-report="${escapeHtml(seat.playerId)}" title="${t("Denunciar a este jugador")}" aria-label="${t("Denunciar a este jugador")}">⚑</button>`
          : "");
      if (free) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "seat-btn";
        btn.dataset.seat = seat.playerId;
        btn.title = t("Ponerte en {equipo}, hueco {n}", { equipo: home ? t("Local") : t("Visitante"), n: slot });
        btn.innerHTML = body;
        li.appendChild(btn);
      } else {
        li.innerHTML = body;
      }
      (home ? seatsHome : seatsAway).appendChild(li);
    }

    const countHome = overlay.querySelector("[data-count-home]") as HTMLElement;
    const countAway = overlay.querySelector("[data-count-away]") as HTMLElement;
    countHome.textContent = `${humansHome}/${indexHome}`;
    countAway.textContent = `${humansAway}/${indexAway}`;

    // El banquillo: quien esta en la sala sin jugar.
    const bench = room.bench ?? [];
    benchList.innerHTML = "";
    benchCount.textContent = bench.length > 0 ? String(bench.length) : "";
    benchEmpty.hidden = bench.length > 0;
    for (const b of bench) {
      const isMe = b.clientId === me;
      const li = document.createElement("li");
      li.className = ["seat", "is-human", "is-bench", isMe ? "is-me" : "", b.away ? "is-away" : ""].filter(Boolean).join(" ");
      const acts =
        isHost && !isMe
          ? (canEnter && freeOn("home") ? actBtn("home", b.clientId, t("L"), t("Meter en Local")) : "") +
            (canEnter && freeOn("away") ? actBtn("away", b.clientId, t("V"), t("Meter en Visitante")) : "") +
            actBtn("kick", b.clientId, "✕", t("Expulsar de la sala"), true)
          : isHost && isMe && canEnter
            ? (freeOn("home") ? actBtn("home", b.clientId, t("L"), t("Meter en Local")) : "") +
              (freeOn("away") ? actBtn("away", b.clientId, t("V"), t("Meter en Visitante")) : "")
            : "";
      li.innerHTML =
        `<span class="seat-slot">·</span>` +
        `<span class="seat-nick">${escapeHtml(b.nickname)}</span>` +
        `<span class="seat-tag">${isMe ? t("tú") : b.clientId === room.hostId ? t("anfitrión") : b.away ? t("en resultados") : t("espera")}</span>` +
        (acts ? `<span class="seat-acts">${acts}</span>` : "");
      benchList.appendChild(li);
    }

    // El partido en marcha, dicho arriba: para quien espera es lo primero que
    // quiere saber — que pasa y cuando le toca.
    liveBanner.classList.toggle("is-hidden", !playing);
    if (playing) {
      const when = runningFor(room);
      if (mySeat) liveText.textContent = t("Partido en juego · {min}", { min: when });
      else if (isHost)
        liveText.textContent = canEnter
          ? t("Partido en juego · {min}. Puedes meter a alguien del banquillo o pararlo.", { min: when })
          : t("Partido en juego · {min}. Ya no se puede entrar: los del banquillo juegan el siguiente.", { min: when });
      else
        liveText.textContent = canEnter
          ? t("Partido en juego · {min}. Entras cuando el anfitrión te meta o al acabar.", { min: when })
          : t("Partido en juego · {min}. Juegas el siguiente: el anfitrión decide quién entra.", { min: when });
    }

    seatNote.textContent = playing
      ? ""
      : isHost
        ? t("Eres el anfitrión: tú decides quién juega. Los huecos vacíos los lleva la IA.")
        : t("Pulsa un hueco libre para cambiarte. Los huecos vacíos los lleva la IA.");

    // Moverse uno mismo: al banquillo (dejar sitio) o a jugar, en el lobby.
    selfSeatBtn.hidden = playing || !online;
    if (!playing) {
      const anyFree = seatsOfMode.some((s) => !room.seats[s.playerId]);
      selfSeatBtn.textContent = mySeat ? t("Al banquillo") : t("Sentarme");
      selfSeatBtn.disabled = !mySeat && !anyFree;
      selfSeatBtn.dataset.to = mySeat ? "bench" : "seat";
    }

    syncInvite(room);
    startBtn.classList.toggle("is-danger", playing && isHost);
    if (playing) {
      startBtn.disabled = !isHost || !online;
      startBtn.textContent = isHost ? t("Parar partido") : t("Partido en juego…");
    } else {
      const anyone = Object.values(room.seats).some(Boolean);
      startBtn.disabled = !isHost || !online || !anyone;
      startBtn.textContent = isHost
        ? anyone
          ? t("Jugar")
          : t("Sienta a alguien para jugar")
        : t("Esperando al anfitrión…");
    }
    chatAside.hidden = !chat;
    syncInviteFriends(room);
  };

  /** Ordenes del anfitrion (y moverse uno mismo) sobre las filas. */
  const onActClick = (event: Event): void => {
    const btn = (event.target as HTMLElement | null)?.closest("[data-act]");
    if (!(btn instanceof HTMLElement) || !activeRoomId) return;
    event.stopPropagation();
    const target = btn.dataset.client;
    const act = btn.dataset.act;
    if (!target || !act) return;
    if (act === "kick") {
      store.kick?.(activeRoomId, target);
      return;
    }
    store.move?.(activeRoomId, target, act === "bench" ? null : act === "home" ? "home" : "away");
  };
  for (const col of [seatsHome, seatsAway, benchList]) col.addEventListener("click", onActClick);

  selfSeatBtn.addEventListener("click", () => {
    if (!activeRoomId || !shownRoom) return;
    if (selfSeatBtn.dataset.to === "bench") {
      store.move?.(activeRoomId, store.clientId, null);
      return;
    }
    // Al mejor hueco que quede, del equipo con mas sitio.
    const seatsOfMode = fieldSeatsForMode(shownRoom.modeId);
    const freeHome = seatsOfMode.filter((s) => s.side === "home" && !shownRoom!.seats[s.playerId]).length;
    const freeAway = seatsOfMode.filter((s) => s.side === "away" && !shownRoom!.seats[s.playerId]).length;
    store.move?.(activeRoomId, store.clientId, freeAway > freeHome ? "away" : "home");
  });

  // --- chat de la sala ----------------------------------------------------
  const renderChat = (entries: readonly RoomChatEntry[]): void => {
    const nearBottom = chatLog.scrollHeight - chatLog.scrollTop - chatLog.clientHeight < 40;
    chatLog.replaceChildren(...entries.map((e) => chatLineNode(e, store.clientId)));
    if (entries.length === 0) {
      const empty = document.createElement("li");
      empty.className = "chat-line is-system";
      empty.textContent = t("Aún no ha escrito nadie. Saluda.");
      chatLog.appendChild(empty);
    }
    // Se baja solo si ya estabas abajo: si subiste a leer algo, no se te mueve.
    if (nearBottom) chatLog.scrollTop = chatLog.scrollHeight;
  };
  const unsubChat = chat?.subscribe((entries) => renderChat(entries)) ?? null;
  chatForm.addEventListener("submit", (event) => {
    event.preventDefault();
    if (!chat) return;
    chat.send(chatInput.value);
    chatInput.value = "";
    chatLog.scrollTop = chatLog.scrollHeight;
  });

  // ================================================================ social
  const shell = overlay.querySelector("[data-lobby-shell]") as HTMLElement;
  const lobbyLog = overlay.querySelector("[data-lobby-log]") as HTMLOListElement;
  const lobbyForm = overlay.querySelector("[data-lobby-form]") as HTMLFormElement;
  const lobbyInput = overlay.querySelector("[data-lobby-input]") as HTMLInputElement;
  const chatHere = overlay.querySelector("[data-chat-here]") as HTMLElement;
  const friendsOnline = overlay.querySelector("[data-friends-online]") as HTMLElement;
  const badgeChat = overlay.querySelector("[data-badge-chat]") as HTMLElement;
  const badgeFriends = overlay.querySelector("[data-badge-friends]") as HTMLElement;
  const badgeRequests = overlay.querySelector("[data-badge-requests]") as HTMLElement;
  const friendCode = overlay.querySelector("[data-friend-code]") as HTMLElement;
  const friendCodeCopy = overlay.querySelector("[data-friend-code-copy]") as HTMLButtonElement;
  const friendAdd = overlay.querySelector("[data-friend-add]") as HTMLFormElement;
  const friendAddInput = overlay.querySelector("[data-friend-add-input]") as HTMLInputElement;
  const friendLists = overlay.querySelector("[data-friend-lists]") as HTMLElement;

  /**
   * Que se ve. En escritorio las salas estan siempre y a la derecha va el chat
   * o los amigos (`data-social`); en movil una sola cosa a la vez
   * (`data-view`). Es el mismo estado leido por dos anchos de pantalla.
   */
  let unreadChat = 0;
  const setView = (view: "rooms" | "chat" | "friends"): void => {
    shell.dataset.view = view;
    if (view !== "rooms") shell.dataset.social = view;
    for (const tab of overlay.querySelectorAll<HTMLElement>("[data-go]")) {
      tab.classList.toggle("is-active", tab.dataset.go === view);
    }
    const social = shell.dataset.social;
    for (const tab of overlay.querySelectorAll<HTMLElement>("[data-social-tab]")) {
      tab.classList.toggle("is-active", tab.dataset.socialTab === social);
    }
    for (const pane of overlay.querySelectorAll<HTMLElement>("[data-social-pane]")) {
      pane.hidden = pane.dataset.socialPane !== social;
    }
    if (social === "chat" && (view === "chat" || !isNarrow())) {
      unreadChat = 0;
      syncBadges();
      lobbyLog.scrollTop = lobbyLog.scrollHeight;
    }
    syncDmFocus();
  };
  const isNarrow = (): boolean => window.matchMedia("(max-width: 899px)").matches;
  for (const tab of overlay.querySelectorAll<HTMLElement>("[data-go]")) {
    tab.addEventListener("click", () => setView(tab.dataset.go as "rooms" | "chat" | "friends"));
  }
  for (const tab of overlay.querySelectorAll<HTMLElement>("[data-social-tab]")) {
    tab.addEventListener("click", () => setView(tab.dataset.socialTab as "chat" | "friends"));
  }

  const syncBadges = (): void => {
    badgeChat.hidden = unreadChat === 0;
    badgeChat.textContent = unreadChat > 9 ? "9+" : String(unreadChat);
    // Solicitudes y privados sin leer: las dos cosas esperan a que hagas algo.
    const pending = (friendsStore?.state()?.incoming.length ?? 0) + (dms?.totalUnread() ?? 0);
    for (const badge of [badgeFriends, badgeRequests]) {
      badge.hidden = pending === 0;
      badge.textContent = pending > 9 ? "9+" : String(pending);
    }
    const online = friendsStore?.onlineCount() ?? 0;
    friendsOnline.textContent = online > 0 ? `· ${online}` : "";
  };

  // --- menu de una persona (pulsar su nombre en el chat) -----------------
  let personMenu: HTMLElement | null = null;
  const closePersonMenu = (): void => {
    personMenu?.remove();
    personMenu = null;
  };
  const openPersonMenu = (anchor: HTMLElement, clientId: string, nickname: string): void => {
    closePersonMenu();
    if (!friendsStore || clientId === store.clientId) return;
    const menu = document.createElement("div");
    menu.className = "person-menu";
    const title = document.createElement("strong");
    title.textContent = nickname;
    menu.appendChild(title);
    const add = document.createElement("button");
    add.type = "button";
    const already = friendsStore.isFriend(clientId);
    add.textContent = already ? t("Ya sois amigos") : t("Añadir amigo");
    add.disabled = already;
    add.addEventListener("click", () => {
      friendsStore.addById(clientId);
      closePersonMenu();
    });
    menu.appendChild(add);
    if (already && dms) {
      const write = document.createElement("button");
      write.type = "button";
      write.textContent = t("Mensaje privado");
      write.addEventListener("click", () => {
        closePersonMenu();
        openDm(clientId);
      });
      menu.appendChild(write);
    }
    const blocked = friendsStore.isBlocked(clientId);
    const block = document.createElement("button");
    block.type = "button";
    block.textContent = blocked ? t("Desbloquear") : t("Bloquear");
    block.addEventListener("click", () => {
      if (blocked) friendsStore.unblock(clientId);
      else {
        friendsStore.block(clientId);
        showLobbyToast(t("Has bloqueado a {nick}. Puedes desbloquearle en Amigos.", { nick: nickname }));
      }
      closePersonMenu();
    });
    menu.appendChild(block);
    const rect = anchor.getBoundingClientRect();
    menu.style.left = `${Math.min(rect.left, window.innerWidth - 200)}px`;
    menu.style.top = `${rect.bottom + 4}px`;
    overlay.appendChild(menu);
    personMenu = menu;
  };
  overlay.addEventListener("click", (event) => {
    if (personMenu && !personMenu.contains(event.target as Node)) closePersonMenu();
  });

  // --- chat del lobby -----------------------------------------------------
  const renderLobbyChat = (entries: readonly RoomChatEntry[]): void => {
    const nearBottom = lobbyLog.scrollHeight - lobbyLog.scrollTop - lobbyLog.clientHeight < 40;
    // A quien tienes bloqueado no se le lee: ni en el lobby.
    const visible = entries.filter((e) => !e.from || !friendsStore?.isBlocked(e.from));
    lobbyLog.replaceChildren(
      ...visible.map((e) => {
        const line = chatLineNode(e, store.clientId);
        const nick = line.querySelector(".chat-nick") as HTMLElement | null;
        if (nick && e.from && e.from !== store.clientId) {
          nick.classList.add("is-clickable");
          nick.tabIndex = 0;
          nick.addEventListener("click", (ev) => {
            ev.stopPropagation();
            openPersonMenu(nick, e.from!, e.nickname);
          });
        }
        return line;
      }),
    );
    if (entries.length === 0) {
      const empty = document.createElement("li");
      empty.className = "chat-line is-system";
      empty.textContent = t("Nadie ha escrito todavía. Di qué quieres jugar y quedáis en una sala.");
      lobbyLog.appendChild(empty);
    }
    if (nearBottom) lobbyLog.scrollTop = lobbyLog.scrollHeight;
  };
  const unsubLobbyChat =
    lobbyChat?.subscribe((entries, added) => {
      renderLobbyChat(entries);
      const here = lobbyChat.here();
      chatHere.textContent = here > 0 ? `· ${here}` : "";
      const visible = shell.dataset.social === "chat" && (!isNarrow() || shell.dataset.view === "chat");
      if (!visible) {
        unreadChat += added.filter((e) => e.from !== store.clientId).length;
        syncBadges();
      }
    }) ?? null;
  lobbyForm.addEventListener("submit", (event) => {
    event.preventDefault();
    lobbyChat?.send(lobbyInput.value);
    lobbyInput.value = "";
  });
  if (!lobbyChat) overlay.querySelector(".lobby-col--social")?.setAttribute("hidden", "");

  // --- amigos ---------------------------------------------------------------

  const btn = (label: string, cls: string, run: () => void, title?: string): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = cls;
    b.textContent = label;
    if (title) {
      b.title = title;
      b.setAttribute("aria-label", title);
    }
    b.addEventListener("click", (ev) => {
      ev.stopPropagation();
      run();
    });
    return b;
  };


  const friendById = (id: string): FriendEntry | null =>
    friendsStore?.state()?.friends.find((f) => f.clientId === id) ?? null;

  const joinFriendRoom = (f: FriendEntry): void => {
    if (!f.roomId) return;
    const room = store.get(f.roomId);
    if (room) void tryJoinRoom(room);
    else joinById(f.roomId);
  };

  // --- privados -----------------------------------------------------------
  const friendsPane = overlay.querySelector(".lobby-friends") as HTMLElement;
  const dmBox = overlay.querySelector("[data-dm]") as HTMLElement;
  const dmName = overlay.querySelector("[data-dm-name]") as HTMLElement;
  const dmStatus = overlay.querySelector("[data-dm-status]") as HTMLElement;
  const dmActions = overlay.querySelector("[data-dm-actions]") as HTMLElement;
  const dmMenu = overlay.querySelector("[data-dm-menu]") as HTMLElement;
  const dmLog = overlay.querySelector("[data-dm-log]") as HTMLOListElement;
  const dmForm = overlay.querySelector("[data-dm-form]") as HTMLFormElement;
  const dmInput = overlay.querySelector("[data-dm-input]") as HTMLInputElement;
  /** Con quien se esta hablando en el panel, o null si se ve la lista. */
  let dmPeer: string | null = null;
  /** Quitar y bloquear piden dos toques: faciles de hacer sin querer. */
  let dmArmed: "remove" | "block" | null = null;
  let dmArmTimer = 0;

  const dmVisible = (): boolean =>
    dmPeer !== null &&
    !overlay.classList.contains("is-hidden") &&
    shell.dataset.social === "friends" &&
    (!isNarrow() || shell.dataset.view === "friends");

  /** Solo cuenta como leido lo que esta a la vista. */
  const syncDmFocus = (): void => {
    if (!dms) return;
    if (dmPeer && dmVisible()) dms.open(dmPeer);
    else dms.close();
  };

  const openDm = (clientId: string): void => {
    if (!dms) {
      const f = friendById(clientId);
      if (f) options.openFriend?.(f.clientId, f.nickname);
      return;
    }
    dmPeer = clientId;
    dmArmed = null;
    friendsPane.classList.add("is-chatting");
    dmBox.hidden = false;
    dmMenu.hidden = true;
    setView("friends");
    syncDmFocus();
    renderDm();
    dmLog.scrollTop = dmLog.scrollHeight;
    if (!window.matchMedia?.("(pointer: coarse)").matches) dmInput.focus();
  };

  const closeDm = (): void => {
    dmPeer = null;
    dmArmed = null;
    dms?.close();
    friendsPane.classList.remove("is-chatting");
    dmBox.hidden = true;
    renderFriends(friendsStore?.state() ?? null);
  };
  (overlay.querySelector("[data-dm-back]") as HTMLElement).addEventListener("click", closeDm);

  const armDm = (which: "remove" | "block"): void => {
    dmArmed = which;
    window.clearTimeout(dmArmTimer);
    dmArmTimer = window.setTimeout(() => {
      dmArmed = null;
      renderDm();
    }, 3000);
    renderDm();
  };

  const renderDm = (): void => {
    if (!dmPeer || !dms) return;
    const f = friendById(dmPeer);
    if (!f) {
      // Ya no es tu amigo (te quito, o le quitaste en otra pestaña). Si la
      // lista aun no ha llegado, se espera: se abre desde la ficha antes de
      // que el lobby tenga amigos.
      if (friendsStore?.state()) closeDm();
      return;
    }
    dmName.textContent = f.nickname;
    dmStatus.textContent = whereText(f);
    dmStatus.dataset.status = f.status;
    dmInput.placeholder = t("Escribe a {nick}…", { nick: f.nickname });

    dmActions.replaceChildren();
    if (f.roomId && f.roomOpen && f.roomId !== activeRoomId) {
      dmActions.appendChild(btn(t("Unirse"), "friend-act is-yes", () => joinFriendRoom(f)));
    }
    if (activeRoomId && f.status !== "offline" && f.roomId !== activeRoomId) {
      const roomId = activeRoomId;
      dmActions.appendChild(
        btn(t("Invitar"), "friend-act", () => {
          friendsStore?.invite(roomId, f.clientId);
          showLobbyToast(t("Invitación enviada a {nick}", { nick: f.nickname }));
        }),
      );
    }
    if (options.openFriend) {
      dmActions.appendChild(btn(t("Ficha"), "friend-act", () => options.openFriend?.(f.clientId, f.nickname), t("Ver su ficha")));
    }
    dmActions.appendChild(
      btn("⋯", "friend-act", () => {
        dmMenu.hidden = !dmMenu.hidden;
      }, t("Más opciones")),
    );

    dmMenu.replaceChildren(
      btn(dmArmed === "remove" ? t("¿Seguro? Pulsa otra vez") : t("Quitar de amigos"), `friend-act${dmArmed === "remove" ? " is-armed" : ""}`, () => {
        if (dmArmed !== "remove") {
          armDm("remove");
          return;
        }
        friendsStore?.remove(f.clientId);
        showLobbyToast(t("{nick} ya no está en tus amigos", { nick: f.nickname }));
        closeDm();
      }),
      btn(dmArmed === "block" ? t("¿Seguro? Pulsa otra vez") : t("Bloquear"), `friend-act${dmArmed === "block" ? " is-armed" : ""}`, () => {
        if (dmArmed !== "block") {
          armDm("block");
          return;
        }
        friendsStore?.block(f.clientId);
        showLobbyToast(t("Has bloqueado a {nick}", { nick: f.nickname }));
        closeDm();
      }),
    );
    const hint = document.createElement("p");
    hint.className = "friend-chat-hint";
    hint.textContent = t("Quitarle o bloquearle borra esta conversación. Si le bloqueas, no te llegará nada suyo: ni solicitudes, ni invitaciones, ni mensajes.");
    dmMenu.appendChild(hint);

    const nearBottom = dmLog.scrollHeight - dmLog.scrollTop - dmLog.clientHeight < 40;
    const entries = dms.conversation(dmPeer);
    const lines: HTMLElement[] = [];
    if (dms.hasMore(dmPeer)) {
      const li = document.createElement("li");
      li.className = "chat-line is-system";
      li.appendChild(btn(t("Ver mensajes anteriores"), "friend-act", () => dms.loadOlder(f.clientId)));
      lines.push(li);
    }
    for (const e of entries) {
      lines.push(
        chatLineNode(
          { id: e.id, at: e.at, from: e.from, nickname: e.from === store.clientId ? nick() : f.nickname, text: e.text },
          store.clientId,
        ),
      );
    }
    if (entries.length === 0) {
      const li = document.createElement("li");
      li.className = "chat-line is-system";
      li.textContent = dms.loaded(dmPeer)
        ? t("Aún no os habéis escrito. Rompe el hielo: «¿echamos unas partidas?»")
        : t("Cargando…");
      lines.push(li);
    }
    dmLog.replaceChildren(...lines);
    if (nearBottom) dmLog.scrollTop = dmLog.scrollHeight;
  };

  dmForm.addEventListener("submit", (event) => {
    event.preventDefault();
    if (!dmPeer) return;
    dms?.send(dmPeer, dmInput.value);
    dmInput.value = "";
  });

  const renderFriends = (state: FriendsState | null): void => {
    syncBadges();
    friendCode.textContent = state?.code ?? "····-····";
    if (dmPeer) renderDm();
    friendLists.replaceChildren();
    if (!state) return;
    const section = (title: string, count: number): HTMLElement => {
      const h = document.createElement("h3");
      h.className = "friend-section";
      h.textContent = count > 0 ? `${title} · ${count}` : title;
      friendLists.appendChild(h);
      const ul = document.createElement("ul");
      ul.className = "friend-list";
      friendLists.appendChild(ul);
      return ul;
    };

    if (state.incoming.length > 0) {
      const ul = section(t("Solicitudes"), state.incoming.length);
      for (const r of state.incoming) {
        const li = document.createElement("li");
        li.className = "friend-row is-request";
        const name = document.createElement("span");
        name.className = "friend-name";
        name.textContent = r.nickname;
        li.append(
          name,
          btn(t("Aceptar"), "friend-act is-yes", () => friendsStore?.respond(r.clientId, true)),
          btn("✕", "friend-act", () => friendsStore?.respond(r.clientId, false), t("Rechazar")),
        );
        ul.appendChild(li);
      }
    }

    const ul = section(t("Amigos"), state.friends.length);
    if (state.friends.length === 0) {
      const li = document.createElement("li");
      li.className = "friend-empty";
      li.textContent = t("Escribe arriba el apodo de alguien, pásale tu código, o pulsa su nombre en el chat para añadirle.");
      ul.appendChild(li);
    }
    // Primero quien te ha escrito; dentro de eso, el orden del servidor
    // (conectados antes, luego por nombre).
    const friends = [...state.friends].sort((a, b) => (dms?.unread(b.clientId) ?? 0) - (dms?.unread(a.clientId) ?? 0));
    for (const f of friends) {
      const li = document.createElement("li");
      li.className = `friend-row is-${f.status} is-clickable`;
      li.tabIndex = 0;
      li.setAttribute("role", "button");
      li.title = dms ? t("Escribirle") : t("Ver su ficha");
      li.addEventListener("click", () => openDm(f.clientId));
      li.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          openDm(f.clientId);
        }
      });
      const dot = document.createElement("i");
      dot.className = "friend-dot";
      const info = document.createElement("span");
      info.className = "friend-info";
      const name = document.createElement("span");
      name.className = "friend-name";
      name.textContent = f.nickname;
      const mark = tierMarkNode(f.tier);
      if (mark) name.prepend(mark);
      const chip = vsChip(f);
      if (chip) {
        const vs = document.createElement("span");
        vs.className = `friend-vs is-${chip.tone}`;
        vs.title = chip.title;
        vs.textContent = `⚔ ${chip.text}`;
        name.append(" ", vs);
      }
      const where = document.createElement("small");
      where.className = "friend-where";
      where.textContent = whereText(f);
      info.append(name, where);
      li.append(dot, info);
      const unread = dms?.unread(f.clientId) ?? 0;
      if (unread > 0) {
        const badge = document.createElement("i");
        badge.className = "friend-unread";
        badge.textContent = unread > 9 ? "9+" : String(unread);
        badge.title = t("{n} sin leer", { n: unread });
        li.appendChild(badge);
      }
      const withMe = activeRoomId !== null && f.roomId === activeRoomId;
      if (f.roomId && !withMe && f.roomOpen) {
        li.appendChild(btn(t("Unirse"), "friend-act is-yes", () => joinFriendRoom(f)));
      } else if (f.status !== "offline" && !withMe) {
        // Conectado y sin sala a la que ir: se monta una (o se le invita a la tuya).
        li.appendChild(btn(activeRoomId ? t("Invitar") : t("Jugar"), "friend-act is-yes", () => playWith(f.clientId)));
      }
      ul.appendChild(li);
    }

    /*
     * Gente de tus ultimos partidos que aun no es tu amiga: la forma natural
     * de hacer amigos en un juego es jugar con alguien y querer repetir.
     */
    const known = new Set([
      store.clientId,
      ...state.friends.map((f) => f.clientId),
      ...state.incoming.map((r) => r.clientId),
      ...state.outgoing.map((r) => r.clientId),
      ...state.blocked.map((r) => r.clientId),
    ]);
    const recent = loadRecentPlayers().filter((p) => !known.has(p.clientId)).slice(0, 6);
    if (recent.length > 0) {
      const rec = section(t("Jugaste con"), 0);
      for (const p of recent) {
        const li = document.createElement("li");
        li.className = "friend-row is-recent";
        const info = document.createElement("span");
        info.className = "friend-info";
        const name = document.createElement("span");
        name.className = "friend-name";
        name.textContent = p.nickname;
        const when = document.createElement("small");
        when.className = "friend-where";
        when.textContent = lastSeenText(p.at);
        info.append(name, when);
        li.append(info, btn(t("+ Añadir"), "friend-act is-yes", () => friendsStore?.addById(p.clientId)));
        rec.appendChild(li);
      }
    }

    if (state.outgoing.length > 0) {
      const out = section(t("Enviadas"), state.outgoing.length);
      for (const r of state.outgoing) {
        const li = document.createElement("li");
        li.className = "friend-row is-pending";
        const name = document.createElement("span");
        name.className = "friend-name";
        name.textContent = r.nickname;
        const hint = document.createElement("small");
        hint.className = "friend-where";
        hint.textContent = t("pendiente");
        li.append(name, hint, btn("✕", "friend-act", () => friendsStore?.remove(r.clientId), t("Cancelar solicitud")));
        out.appendChild(li);
      }
    }

    if (state.blocked.length > 0) {
      // Plegado: es una lista a la que se viene a proposito, no algo que ver.
      const details = document.createElement("details");
      details.className = "friend-blocked";
      const summary = document.createElement("summary");
      summary.textContent = `${t("Bloqueados")} · ${state.blocked.length}`;
      details.appendChild(summary);
      const list = document.createElement("ul");
      list.className = "friend-list";
      for (const r of state.blocked) {
        const li = document.createElement("li");
        li.className = "friend-row is-blocked";
        const name = document.createElement("span");
        name.className = "friend-name";
        name.textContent = r.nickname;
        li.append(name, btn(t("Desbloquear"), "friend-act", () => friendsStore?.unblock(r.clientId)));
        list.appendChild(li);
      }
      details.appendChild(list);
      friendLists.appendChild(details);
    }

    // Lo de las salas depende de donde esten los amigos: se repinta.
    renderList(lastRooms);
    if (shownRoom) syncInviteFriends(shownRoom);
    // Y el chat del lobby, por si ha cambiado a quien tienes bloqueado.
    if (lobbyChat) renderLobbyChat(lobbyChat.entries());
  };

  const unsubFriends = friendsStore?.subscribe(renderFriends) ?? null;
  const unsubDms =
    dms?.subscribe(() => {
      if (dmPeer) renderDm();
      else renderFriends(friendsStore?.state() ?? null);
      syncBadges();
    }) ?? null;
  const unsubDmIncoming =
    dms?.onIncoming((peer, entry) => {
      if (overlay.classList.contains("is-hidden")) return;
      const f = friendById(peer);
      if (!f) return;
      showLobbyToast(`✉ ${f.nickname}: ${entry.text}`, { label: t("Responder"), run: () => openDm(peer) });
    }) ?? null;
  const unsubFriendEvents =
    friendsStore?.onEvent((event) => {
      if (overlay.classList.contains("is-hidden")) return;
      // Solicitudes, amistades nuevas e invitaciones las avisa la capa social
      // (`socialLayer.ts`), en esta pantalla y en todas. Aqui, lo que contesta
      // a algo que has hecho tu en el lobby.
      if (event.kind === "result") {
        const text: Record<string, string> = {
          sent: t("Solicitud enviada"),
          accepted: t("¡Ya sois amigos!"),
          already: t("Ya sois amigos"),
          pending: t("Ya se la habías pedido"),
          self: t("Ese eres tú"),
          not_found: t("No hay nadie con ese apodo o código"),
          limit: t("Límite de amigos alcanzado"),
          slow: t("Espera un poco antes de enviar más solicitudes"),
          blocked: t("Lo tienes bloqueado: desbloquéalo antes en Amigos"),
        };
        const bad = ["self", "not_found", "limit", "slow", "blocked"].includes(event.result);
        showLobbyToast(text[event.result] ?? "", undefined, bad ? "error" : "info");
        if (event.result === "sent" || event.result === "accepted") friendAddInput.value = "";
      }
    }) ?? null;

  friendAdd.addEventListener("submit", (event) => {
    event.preventDefault();
    const text = friendAddInput.value.trim();
    if (text) friendsStore?.addByText(text);
  });
  friendCodeCopy.addEventListener("click", () => {
    const code = friendsStore?.state()?.code;
    if (!code) return;
    void navigator.clipboard?.writeText(code).then(
      () => showLobbyToast(t("Código copiado: {code}", { code })),
      () => showLobbyToast(code),
    );
  });
  if (!friendsStore) overlay.querySelector("[data-social-tab='friends']")?.setAttribute("hidden", "");

  // --- invitar a amigos desde la sala ---------------------------------------
  const inviteFriendsBox = document.createElement("section");
  inviteFriendsBox.className = "room-invite-friends";
  overlay.querySelector(".room-bench")?.after(inviteFriendsBox);
  const invited = new Set<string>();
  function syncInviteFriends(room: OnlineRoom): void {
    const inRoom = new Set([
      ...Object.values(room.seats).map((o) => o?.clientId),
      ...(room.bench ?? []).map((b) => b.clientId),
    ]);
    const candidates = (friendsStore?.state()?.friends ?? []).filter(
      (f) => f.status !== "offline" && !inRoom.has(f.clientId),
    );
    inviteFriendsBox.replaceChildren();
    inviteFriendsBox.hidden = candidates.length === 0;
    if (candidates.length === 0) return;
    const h = document.createElement("h3");
    h.className = "room-bench-head";
    h.textContent = t("Invitar amigos");
    inviteFriendsBox.appendChild(h);
    const row = document.createElement("div");
    row.className = "invite-chips";
    const unsent = candidates.filter((f) => !invited.has(`${room.id}:${f.clientId}`));
    if (unsent.length >= 2) {
      row.appendChild(
        btn(t("Invitar a todos ({n})", { n: unsent.length }), "invite-chip is-all", () => {
          for (const f of unsent) {
            friendsStore?.invite(room.id, f.clientId);
            invited.add(`${room.id}:${f.clientId}`);
          }
          syncInviteFriends(room);
        }),
      );
    }
    for (const f of candidates) {
      const sent = invited.has(`${room.id}:${f.clientId}`);
      const chip = btn(sent ? `✓ ${f.nickname}` : `+ ${f.nickname}`, `invite-chip${sent ? " is-sent" : ""}`, () => {
        friendsStore?.invite(room.id, f.clientId);
        invited.add(`${room.id}:${f.clientId}`);
        syncInviteFriends(room);
      }, t("Invitar a {nick}", { nick: f.nickname }));
      chip.disabled = sent;
      row.appendChild(chip);
    }
    inviteFriendsBox.appendChild(row);
  }

  unsub = store.subscribe((rooms) => {
    renderList(rooms);

    if (awaitingCreate) {
      openOwnedCreate(rooms);
    }

    /*
     * La sala se pinta con un id provisional antes de que conteste el server, y
     * ese id no sirve para nada real: ni para el enlace de invitacion ni para
     * seguir la sala. Cuando aparece la de verdad hay que saltar a ella.
     *
     * Sin esto, `activeRoomId` se quedaba apuntando al id provisional y el
     * siguiente sync no encontraba la sala: el modal se cerraba solo.
     */
    if (awaitingJoinId) {
      const joined = rooms.find((r) => r.id === awaitingJoinId);
      if (joined) {
        awaitingJoinId = null;
        activeRoomId = joined.id;
        openModal(joined);
      }
    }

    if (activeRoomId?.startsWith("pending-")) {
      const real = rooms.find(
        (r) => !r.id.startsWith("pending-") && r.hostId === store.clientId && r.status === "lobby",
      );
      if (real) {
        activeRoomId = real.id;
        openModal(real);
      }
    }

    flushPlayWith();

    if (activeRoomId) {
      const room = rooms.find((r) => r.id === activeRoomId);
      if (!room) {
        modal.classList.add("is-hidden");
        activeRoomId = null;
        return;
      }

      if (!client && room.status === "playing" && room.hostId !== store.clientId) {
        const localSeat = Object.entries(room.seats).find(([, o]) => o?.clientId === store.clientId);
        if (localSeat) {
          finish({
            room,
            nickname: nick(),
            localPlayerId: localSeat[0],
            seed: Date.now() >>> 0,
            roomId: room.id,
          });
        }
        return;
      }

      syncModal(room);
      modal.classList.remove("is-hidden");
    }
  });

  createOpenBtn.addEventListener("click", () => openCreateModal());
  inviteOpenBtn.addEventListener("click", () => createInviteRoom());
  inviteCopyBtn.addEventListener("click", () => void copyInvite());
  inviteShareBtn.addEventListener("click", shareInvite);
  inviteUrlInput.addEventListener("focus", () => inviteUrlInput.select());
  createSubmitBtn.addEventListener("click", () => void submitCreate());
  overlay.querySelectorAll("[data-create-close]").forEach((el) => {
    el.addEventListener("click", () => closeCreateModal());
  });
  overlay.querySelectorAll("[data-password-close]").forEach((el) => {
    el.addEventListener("click", () => closePasswordModal(null));
  });
  passwordSubmitBtn.addEventListener("click", () => closePasswordModal(passwordInput.value));
  passwordInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") closePasswordModal(passwordInput.value);
  });
  roomNameInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") void submitCreate();
  });

  overlay.querySelector(".lobby-refresh")?.addEventListener("click", () => store.refresh());
  overlay.querySelector(".lobby-back")?.addEventListener("click", () => finish({ back: true }));

  overlay.querySelector("[data-leave]")?.addEventListener("click", () => {
    if (activeRoomId) store.leave(activeRoomId);
    activeRoomId = null;
    shownRoom = null;
    chat?.clear();
    modal.classList.add("is-hidden");
  });

  overlay.querySelector("[data-start]")?.addEventListener("click", () => {
    if (!activeRoomId || connectionState !== "online") return;
    if (shownRoom?.status === "playing") {
      if (shownRoom.hostId === store.clientId) store.stop?.(activeRoomId);
      return;
    }
    if (client) {
      store.markPlaying(activeRoomId);
      return;
    }
    const room = store.markPlaying(activeRoomId);
    if (!room) return;
    const localSeat = Object.entries(room.seats).find(([, o]) => o?.clientId === store.clientId);
    if (!localSeat) return;
    finish({
      room,
      nickname: nick(),
      localPlayerId: localSeat[0],
      seed: Date.now() >>> 0,
      roomId: room.id,
    });
  });

  hide();

  return {
    show(opts: ShowOptions = {}) {
      overlay.classList.remove("is-hidden");
      startRefresh();
      lobbyChat?.enter();
      friendsStore?.refresh();
      setView(shell.dataset.view === "rooms" ? "rooms" : (shell.dataset.view as "chat" | "friends"));

      return new Promise<OnlinePlayRequest | { back: true }>((resolve) => {
        resolvePick = resolve;

        const boot = client && client.state === "offline" ? client.connect().catch(() => {}) : Promise.resolve();
        void boot.then(() => {
          store.refresh();
          if (opts.notice) showLobbyToast(opts.notice);
          // Se llega por enlace, o se vuelve a la sala: entrar es la unica
          // accion que tiene sentido.
          const target = opts.roomId ?? opts.joinRoomId;
          if (target) {
            joinById(target);
            return;
          }
          if (SHOW_WEEKLY) {
            syncWeekly();
            // La cuenta atras se refresca sola: un lobby abierto media hora no
            // puede seguir diciendo lo mismo.
            clearInterval(weeklyTimer);
            weeklyTimer = window.setInterval(syncWeekly, 30_000);
          }
          if (opts.quickMode) selectQuickMode(opts.quickMode);
          if (opts.autoQueue && !searching) startQuickplay();
          if (opts.dm) openDm(opts.dm);
          if (opts.playWith) playWith(opts.playWith);
          if (opts.inviteNew) inviteNew();
        });
      });
    },
    hide,
    isVisible: () => !overlay.classList.contains("is-hidden"),
    openChat: (clientId) => openDm(clientId),
    joinRoom: (roomId, pass = false) => {
      const room = store.get(roomId);
      if (room && !pass) void tryJoinRoom(room);
      else joinById(roomId);
    },
    canInvite: () => activeRoomId !== null,
    inviteFriend(clientId) {
      // "Invitacion enviada" (o "no esta conectado") lo dice el servidor, y lo
      // avisa la capa social.
      if (!activeRoomId) return;
      friendsStore?.invite(activeRoomId, clientId);
    },
    playWith,
    inviteNew,
    dispose() {
      stopRefresh();
      clearTimeout(lobbyToastTimer);
      unsubChat?.();
      unsubLobbyChat?.();
      unsubFriends?.();
      unsubFriendEvents?.();
      unsub?.();
      unsubClient?.();
      unsubState?.();
      unsubNick();
      unsubDms?.();
      unsubDmIncoming?.();
      unsubParty?.();
      window.clearTimeout(dmArmTimer);
      store.dispose();
      overlay.remove();
    },
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
